import {
	DatabaseResponse,
	Connection,
	ProgressCallback,
	GetRecordQuery,
	FetchOptions,
	RecordData,
	Condition,
	UserAttributes,
	UserVisibilityFlag,
	UserProfile,
	Newsletter,
	NewsletterGroup,
	Form,
	PostRecordConfig,
	PostRecordsItem,
	PostRecordsResult,
	UserPublic,
	FileInfo,
	RTCEvent,
	RealtimeCallback,
	RTCConnectorParams,
	RTCConnector,
	DelRecordQuery,
	ConnectionInfo,
	Table,
	Index,
	Tag,
	UniqueId,
	Subscription,
    RequestHistory,
	Ticket,
	TicketCondition,
	TicketAction,
	TicketReceipt,
	UploadFilesParams,
	UploadFilesResult,
} from '../Types';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import SkapiError from './error';
import validator from '../utils/validator';
import {
	getRecords,
	postRecord,
	deleteRecords,
	getTables,
	getIndexes,
	getTags,
	getFile,
	grantPrivateRecordAccess,
	removePrivateRecordAccess,
	listPrivateRecordAccess,
	requestPrivateRecordAccessKey,
	deleteFiles,
	getUniqueId,
	bulkPostRecords,
	postRecords,
} from '../methods/database';
import {
	connectRealtime,
	joinRealtime,
	postRealtime,
	closeRealtime,
	getRealtimeUsers,
	getRealtimeGroups,
} from '../methods/realtime';
import { closeRTC, connectRTC } from '../methods/webrtc';
import {
	secureRequest,
	forwardRequest,
	mock,
	clientSecretRequest,
	clientSecretRequestHistory,
	clientSecretRequestStream,
	clientSecretRequestFinalize,
	sendInquiry,
	cancelClientSecretRequest,
	clientSecretRequestQueueCount,
	stopClientSecretPolling,
	isPollStopped,
	// The forwardRequest family. Each of these IS the function above it, aliased in
	// methods/request rather than wrapped, so the two names cannot drift apart.
	forwardRequestHistory,
	forwardRequestStream,
	forwardRequestFinalize,
	cancelForwardRequest,
	forwardRequestQueueCount,
	stopForwardRequestPolling,
} from '../methods/request';
import {
	request,
	getFormResponse,
	formHandler,
	uploadFiles,
	terminatePendingRequests,
} from '../utils/network';
import {
	subscribe,
	unsubscribe,
	blockSubscriber,
	unblockSubscriber,
	getSubscriptions,
	subscribeNewsletter,
	getNewsletters,
	unsubscribeNewsletter,
	getNewsletterSubscription,
	getFeed,
	// adminNewsletterRequest,
	registerNewsletterGroup,
	deleteNewsletterGroup,
	newsletterGroupEndpoint,
} from '../methods/subscription';
import {
	getProfile,
	logout,
	recoverAccount,
	resendSignupConfirmation,
	authentication,
	login,
	signup,
	disableAccount,
	resetPassword,
	verifyEmail,
	verifyPhoneNumber,
	forgotPassword,
	changePassword,
	updateProfile,
	getUsers,
	lastVerifiedEmail,
	consumeTicket,
	getConsumedTickets,
	getTickets,
	registerTicket,
	unregisterTicket,
	updateTicket,
	clearTicketLog,
	_out,
	openIdLogin,
	loginWithToken,
	anonymousLogin,
	startAnonymousKeepAlive,
} from '../methods/user';
import {
	extractFormData,
	fromBase62,
	generateRandom,
	toBase62,
	MD5,
	isBrowserRuntime,
	isNodeRuntime,
	decodeProjectId,
	formatServiceId,
} from '../utils/utils';
import {
	isProjectIdPlaceholder,
	readSavedProjectId,
	saveProjectId,
	forgetSavedProjectId,
	canAskProjectId,
	askProjectId,
	PROJECT_ID_STORAGE_KEY,
} from '../utils/project_id_input';
import {
	blockAccount,
	unblockAccount,
	deleteAccount,
	inviteUser,
	createAccount,
	grantAccess,
	getInvitations,
	cancelInvitation,
	resendInvitation,
	updateUserAttributes
} from '../methods/admin';
import {
	subscribeNotification,
	vapidPublicKey,
	pushNotification,
	unsubscribeNotification,
} from '../methods/notification';
import { spellcast, dopamine, getspell } from '../methods/vivian';
import {
	parseEncryptionOptions,
	initEncryption,
	clearEncryptionState,
	getEncryptionStatus,
	isWithheld,
	unlockEncryption,
	lockEncryption,
	startDeviceUnlock,
	pinPeerKey,
	takeRecoveryCode,
	unlockWithRecoveryCode,
	regenerateRecoveryCode,
	EncryptionConfig,
} from '../methods/encryption';

declare const __SKAPI_VERSION__: string;

// The constructor's arguments once the Project ID is decoded (see _resolveProject).
type ResolvedProject = {
	service: string;
	owner: string;
	options: Options | any;
	__etc: any;
};

type Options = {
	autoLogin: boolean;
	refetchServiceInfo?: boolean; // bypasses cached service info and always fetch new service info on load.
	requestBatchSize?: number; // default 30. number of requests to be handled in a batch
	// bearerToken?: string; // custom bearer token for authentication
	/**
	 * Enable client-side encryption of `data` on records written to
	 * access_group 'private'. Off by default. See EncryptionOptions.
	 */
	encryption?: boolean | {
		iterations?: number;
		trustPolicy?: 'tofu' | 'strict';
		persistDevice?: boolean;
		minPasswordLength?: number;
		recovery?: 'code' | 'none';
		table?: string;
	};
	eventListener?: {
		onLogin?: (user: UserProfile | null) => void;
		onUserUpdate?: (user: UserProfile | null) => void;
		onBatchProcess?: (process: {
			batchToProcess: number;
			itemsToProcess: number;
			completed: any[];
		}) => void;
	};
};

export default class Skapi {
	// current version
	private __version = __SKAPI_VERSION__;
	service: string;
	owner: string;
	/**
	 * The public project ID: the single two-segment token composed from service + owner
	 * (the same value formatServiceId returns, and the form external tools accept).
	 * Empty string when the service has no uuid owner (e.g. host-owned), since the
	 * compound form cannot be built without one.
	 */
	project_id: string = '';
	session: Record<string, any> | null = null;
	connection: Connection | null = null;
	/**
	 * unique_id -> record_id, bucketed by a "<service>/<owner>" scope key.
	 *
	 * The scope is mandatory. One Skapi instance routinely serves MANY services: the
	 * MCP server keeps a single instance per user and names the target project on every
	 * call, and the dashboard switches projects without reconstructing. A flat map let
	 * the same unique_id from two projects collide on one key, so a post carrying
	 * `reference: "src::<file>"` was silently rewritten to the OTHER project's
	 * record_id and the backend rejected it as non-existent. Never key this by
	 * unique_id alone, and never scope it by `this.service` when the call names its own.
	 */
	private __my_unique_ids: { [serviceKey: string]: { [unique_id: string]: string } } = {};
	private __uniqueIdsPersistTimer: ReturnType<typeof setTimeout> | null = null;
	private userPool: CognitoUserPool | null = null;
	private __socket: Promise<WebSocket> | null = null;

	private host = 'skapi';
	private hostDomain = 'skapi.com';
	private target_cdn = 'd3e9syvbtso631';
	private customApiDomain = 'skapi.dev';
	private requestBatchSize = 50;

	// privates
	private __disabledAccount: string | null = null;
	private __cached_requests: {
		/** Cached url requests */
		[url: string]: {
			/** Array of data stored in hashed params key */
			[hashedParams: string]: DatabaseResponse<any>;
		};
	} = {};

	private __startKeyHistory: {
		/** List of startkeys */
		[url: string]: {
			[hashedParams: string]: string[];
		};
	} = {};
	private __request_signup_confirmation: string | null = null;
	private __private_access_key: {
		[record_id: string]: string;
	} = {};

	// true when session is stored successfully to session storage
	// this property prevents duplicate stores when window closes on some device
	private __class_properties_has_been_cached = false;

	/** Current logged in user object. null if not logged. */
	private __user: UserProfile | null = null;

	get user(): UserProfile | null {
		if (this.__user && Object.keys(this.__user).length) {
			return JSON.parse(JSON.stringify(this.__user));
		} else {
			return null;
		}
	}

	set user(value) {
		// setting user is bypassed
	}

	private _userProfileListeners: Function[] = [];
	private _onLoginListeners: Function[] = [];

	get onLogin(): Function[] {
		return this._onLoginListeners;
	}

	set onLogin(listener: (user: UserProfile | null) => void) {
		if (typeof listener === 'function') {
			this._onLoginListeners.push(listener);
		}
	}

	get onUserUpdate(): Function[] {
		return this._userProfileListeners;
	}

	set onUserUpdate(listener: (user: UserProfile | null) => void) {
		if (typeof listener === 'function') {
			this._userProfileListeners.push(listener);
		}
	}

	private _runOnUserUpdateListeners(user: UserProfile | null) {
		for (let listener of this._userProfileListeners) {
			if (typeof listener === 'function') {
				listener(user);
			}
		}
	}

	private _runOnLoginListeners(user: UserProfile | null) {
		for (let listener of this._onLoginListeners) {
			if (typeof listener === 'function') {
				listener(user);
			}
		}
	}

	private admin_endpoint: Promise<Record<string, any>>;
	private record_endpoint: Promise<Record<string, any>>;

	private _onBatchProcessListeners: ((process: {
		batchToProcess: number;
		itemsToProcess: number;
		completed: any[];
	}) => void)[] = [];

	get onBatchProcess(): ((process: {
		batchToProcess: number;
		itemsToProcess: number;
		completed: any[];
	}) => void)[] {
		return this._onBatchProcessListeners;
	}

	set onBatchProcess(
		listener: (process: {
			batchToProcess: number;
			itemsToProcess: number;
			completed: any[];
		}) => void,
	) {
		if (typeof listener === 'function') {
			this._onBatchProcessListeners.push(listener);
		}
	}

	validate = {
		userId(val: string) {
			try {
				validator.UserId(val);
				return true;
			} catch (err) {
				return false;
			}
		},
		url(val: string | string[]) {
			try {
				validator.Url(val);
				return true;
			} catch (err) {
				return false;
			}
		},
		phoneNumber(val: string) {
			try {
				validator.PhoneNumber(val);
				return true;
			} catch (err) {
				return false;
			}
		},
		birthdate(val: string) {
			try {
				validator.Birthdate(val);
				return true;
			} catch (err) {
				return false;
			}
		},
		email(val: string) {
			try {
				validator.Email(val);
				return true;
			} catch (err) {
				return false;
			}
		},
		params(val: any, schema: Record<string, any>, required?: string[]) {
			return validator.Params(val, schema, required);
		},
	};

	util = {
		MD5,
		generateRandom,
		toBase62,
		fromBase62,
		decodeProjectId,
		formatServiceId,
		extractFormData,
		terminatePendingRequests,
		request: (
			url: string,
			data?: Form<any>,
			options?: {
				fetchOptions?: FetchOptions;
				auth?: boolean;
				method?: string;
				bypassAwaitConnection?: boolean;
				responseType?:
					| 'json'
					| 'blob'
					| 'text'
					| 'arrayBuffer'
					| 'formData'
					| 'document';
				contentType?: string;
				tokenHeaders?: {
					accessToken?: boolean | string;
					idToken?: boolean | string;
				};
				// Both matter only to a CACHEABLE request, and both were reachable
				// at runtime but missing from this type — so the callers that need
				// them (a browser-cached signed-url mint) could not ask for them
				// without a cast, and silently did without:
				//
				// stableGateway pins the record host. Without it the round robin
				// alternates between record_private and record_private_2, which is
				// two urls for one mint, two cache entries, two signed urls, and
				// the same file downloaded twice.
				//
				// revalidate sends Cache-Control: no-cache so the browser REPLACES
				// what it has stored under this exact url, which a cache-busting
				// query param cannot do (a different url leaves the stale entry
				// answering every ordinary call).
				//
				// USELESS CROSS-ORIGIN, and worse than useless: Cache-Control is
				// not a CORS-safelisted request header, and the record gateway's
				// preflight does not list it, so the browser REFUSES TO SEND a
				// request carrying it. Every mint that asked for this failed with
				// a network error rather than being revalidated. Bust the cache
				// with a `nocache` parameter instead (see getFile's
				// mintCacheToken), or add Cache-Control to the gateway's
				// Access-Control-Allow-Headers first.
				stableGateway?: boolean;
				revalidate?: boolean;
			},
		) => request.bind(this)(url, data, options, { ignoreService: true }),
	};

	private __connection: Promise<Connection>;
	private __authConnection: Promise<void>;
	// Set only when the constructor was given the docs' placeholder and is asking
	// for the Project ID. It settles once the id is entered and _start() has run
	// with it. Anything that reads this.service or this.owner before awaiting one
	// of the connection promises waits on it first: formHandler does so for every
	// decorated method, and the few undecorated ones that need it do it themselves.
	private __projectIdInput: Promise<void> | null = null;
	private __network_logs = false;
	// Which per-region endpoint file this SDK boots from:
	// <cdn>/<short_region>/admin-<version>.json and record-<version>.json.
	// v2 adds forward_request (the streaming forwarder's Function URL), which v1
	// has no slot for. This SDK no longer calls that Function URL: forwardRequest
	// now means the queued forwarder, which goes to the csr gateway like every
	// other request. The slot stays published for clients that predate the change,
	// so the version below stays where it is.
	//
	// This version must not move ahead of what is published: the fetch below
	// rejects on a missing file and every call then throws "Service does not
	// exist". Publish with infra/cdn/publish_endpoints.py for EVERY region
	// first, and leave v1 in place while older clients are still in the wild.
	private __endpoint_version = 'v2';
	private __public_identifier = '';
	private bearerToken: string = '';

	private _alert(message: string) {
		if (isBrowserRuntime() && typeof window.alert === 'function') {
			window.alert(message);
		}
	}

	constructor(
		service: string,
		owner?: string | Options,
		options?: Options | any,
		__etc?: any,
	) {
		if (!service || typeof service !== 'string') {
			this._alert('Service ID is required.');
			throw new SkapiError('Service ID is required.', {
				code: 'INVALID_PARAMETER',
			});
		}
		// The docs' copy-paste placeholder was never replaced. Checked FIRST, before any
		// decoding or owner validation: the placeholder is not an id to validate, it
		// asks for one. The Project ID entered for it earlier in this session (browser
		// tab, or Node process) is reused; otherwise the person running the code is
		// asked for it (utils/project_id_input.ts). Only where nobody can be asked, a
		// server or a CI job, does the constructor still refuse to start.
		if (isProjectIdPlaceholder(service)) {
			const saved = readSavedProjectId();
			let project: ResolvedProject = null;
			if (saved) {
				try {
					project = this._resolveProject(saved, owner, options, __etc);
				} catch (err) {
					// Not an id these arguments can start with: drop it and ask.
					forgetSavedProjectId(saved);
				}
			}

			if (project) {
				if (isBrowserRuntime()) {
					console.info(`Skapi: "${service}" was replaced with the Project ID entered earlier in this tab (${saved}). It is kept in sessionStorage under "${PROJECT_ID_STORAGE_KEY}"; delete that entry to enter a different one.`);
				}
				this._start(project);
				// An id that does not reach a project is not kept, so the next page
				// load asks again instead of failing the same way.
				this.__connection.catch(() => forgetSavedProjectId(saved));
			}
			else if (canAskProjectId()) {
				this._startAfterProjectIdInput(service, owner, options, __etc);
			}
			else {
				this._alert(`Replace "${service}" with your actual Project ID.`);
				throw new SkapiError('Project ID is required.', {
					code: 'INVALID_PARAMETER',
				});
			}
			return;
		}

		let project: ResolvedProject;
		try {
			project = this._resolveProject(service, owner, options, __etc);
		} catch (err: any) {
			this._alert(err.message);
			throw err;
		}
		this._start(project);
	}

	// Turns the constructor's arguments into the service and owner the instance
	// runs on. A Project ID carries both (an object in the owner position is then
	// the options); a bare service id takes the owner argument. Throws SkapiError
	// for arguments the SDK cannot start with, WITHOUT alerting, so the Project ID
	// prompt can say the answer is invalid and ask again instead.
	private _resolveProject(
		service: string,
		owner: string | Options,
		options: Options | any,
		__etc: any,
	): ResolvedProject {
		let idSplitLen = service.split('-').length;
		let isV2 = idSplitLen === 2;
		let isV1 = idSplitLen === 7;
		if (isV2 || isV1) {
			try {
				let decoded = decodeProjectId(service);
				if (options && typeof options === 'object') {
					__etc = options;
				}

				if (owner && typeof owner === 'object') {
					options = owner;
				}

				owner = decoded.owner;
				service = decoded.service;
			} catch (err) {
				throw new SkapiError('Service ID is invalid.', {
					code: 'INVALID_PARAMETER',
				});
			}
		}

		// if (!window.sessionStorage) {
		//     throw new SkapiError('Web browser API is not available.', { code: 'NOT_SUPPORTED' });
		// }
		// window.sessionStorage.setItem('__skapi_kiss', 'kiss');
		// if (window.sessionStorage.getItem('__skapi_kiss') !== 'kiss') {
		//     window.alert('Session storage is disabled. Please enable session storage.');
		//     throw new SkapiError('Session storage is disabled. Please enable session storage.', { code: 'SESSION_STORAGE_DISABLED' });
		// }

		// window.sessionStorage.removeItem('__skapi_kiss');

		if (!owner || typeof owner !== 'string') {
			throw new SkapiError('Owner ID is invalid.', {
				code: 'INVALID_PARAMETER',
			});
		}

		if (owner !== this.host) {
			try {
				validator.UserId(owner, '"owner"');
			} catch (err: any) {
				throw new SkapiError('Owner ID is invalid.', {
					code: 'INVALID_PARAMETER',
				});
			}
		}

		return { service, owner, options, __etc };
	}

	// Holds back everything the constructor starts until the Project ID is entered.
	//
	// The constructor starts four chains: the two endpoint fetches, the auth
	// restore and the service connection, and every call on the instance waits on
	// one of them (request() through getEndpoint, most methods through
	// __connection). Each is replaced here by a promise that settles after the id
	// is entered and _start() has started the real chain, and then settles AS that
	// chain. A call made right after `new Skapi('<Project ID>')` therefore waits
	// for the answer instead of going out without a service, and nothing reaches
	// the network before there is an id to send.
	private _startAfterProjectIdInput(
		placeholder: string,
		owner: string | Options,
		options: Options | any,
		__etc: any,
	) {
		const ready = askProjectId(placeholder, (id) => {
			this._resolveProject(id, owner, options, __etc);
		}).then((id) => {
			saveProjectId(id);
			this._start(this._resolveProject(id, owner, options, __etc));
			this.__connection.catch(() => forgetSavedProjectId(id));
		});

		this.__projectIdInput = ready;
		this.admin_endpoint = ready.then(() => this.admin_endpoint);
		this.record_endpoint = ready.then(() => this.record_endpoint);
		this.__authConnection = ready.then(() => this.__authConnection);
		this.__connection = ready.then(() => this.__connection);

		// A cancelled prompt rejects all of these, and nothing is obliged to await
		// them: unhandled, each rejection would end a Node process.
		for (const p of [ready, this.admin_endpoint, this.record_endpoint, this.__authConnection, this.__connection]) {
			p.catch(() => {});
		}
	}

	// Everything the constructor does once the service and owner are known: reads
	// the options, then starts the endpoint fetches, the auth restore and the
	// service connection.
	private _start({ service, owner, options, __etc }: ResolvedProject) {
		this.service = service;
		this.owner = owner;
		try {
			this.project_id = formatServiceId(service, owner);
		} catch (err) {
			// Non-uuid owner (host-owned service): there is no public compound form.
			this.project_id = '';
		}

		let autoLogin = true;
		let refetchServiceInfo = false;
		let encryptionConfig: EncryptionConfig | null = null;
		if (options) {
			if (typeof options.refetchServiceInfo === 'boolean') {
				refetchServiceInfo = options.refetchServiceInfo;
			}
			if (typeof options.autoLogin === 'boolean') {
				autoLogin = options.autoLogin;
			}
			if (options.encryption) {
				encryptionConfig = parseEncryptionOptions(options.encryption);
			}
			if (typeof options.requestBatchSize === 'number') {
				if (options.requestBatchSize < 1) {
					throw new SkapiError(
						'"requestBatchSize" must be greater than 0.',
						{ code: 'INVALID_PARAMETER' },
					);
				}
				this.requestBatchSize = options.requestBatchSize;
			}
		}

		if (
			options?.eventListener &&
			typeof options.eventListener === 'object'
		) {
			if (
				options.eventListener?.onLogin &&
				typeof options.eventListener.onLogin === 'function'
			) {
				this.onLogin = options.eventListener.onLogin;
			}

			if (
				options.eventListener?.onUserUpdate &&
				typeof options.eventListener.onUserUpdate === 'function'
			) {
				this.onUserUpdate = options.eventListener.onUserUpdate;
			}

			if (
				options.eventListener?.onBatchProcess &&
				typeof options.eventListener.onBatchProcess === 'function'
			) {
				this.onBatchProcess = options.eventListener.onBatchProcess;
			}
		}

		// get endpoints

		this.target_cdn = __etc?.target_cdn || this.target_cdn;
		this.hostDomain = __etc?.hostDomain || this.hostDomain;
		this.customApiDomain = __etc?.customApiDomain || this.customApiDomain;

		this.__network_logs = !!__etc?.network_logs;

		const cdn_domain = `https://${this.target_cdn}.cloudfront.net`; // don't change this
		let sreg = service.substring(0, 4);

		this.admin_endpoint = fetch(
			`${cdn_domain}/${sreg}/admin-${this.__endpoint_version}.json`,
		)
			.then((response) => response.blob())
			.then(
				(blob) =>
					new Promise((resolve, reject) => {
						const reader = new FileReader();
						reader.onloadend = () => resolve(reader.result);
						reader.onerror = reject;
						reader.readAsDataURL(blob);
					}),
			)
			.then((data) => {
				try {
					return typeof data === 'string'
						? JSON.parse(atob(data.split(',')[1]))
						: null;
				} catch (err) {
					throw new SkapiError(
						'Service does not exist. Create your service from skapi.com',
						{ code: 'NOT_EXISTS' },
					);
				}
			});

		this.record_endpoint = fetch(
			`${cdn_domain}/${sreg}/record-${this.__endpoint_version}.json`,
		)
			.then((response) => response.blob())
			.then(
				(blob) =>
					new Promise((resolve, reject) => {
						const reader = new FileReader();
						reader.onloadend = () => resolve(reader.result);
						reader.onerror = reject;
						reader.readAsDataURL(blob);
					}),
			)
			.then((data) => {
				try {
					return typeof data === 'string'
						? JSON.parse(atob(data.split(',')[1]))
						: null;
				} catch (err) {
					throw new SkapiError(
						'Service does not exist. Create your service from skapi.com',
						{ code: 'NOT_EXISTS' },
					);
				}
			});

		const hasWindow = isBrowserRuntime();

		if (hasWindow && !window.sessionStorage) {
			this._alert('This browser is not supported.');
			throw new Error(`This browser is not supported.`);
		}

		const restore = hasWindow
			? JSON.parse(
					window.sessionStorage.getItem(`${service}#${owner}`) ||
						'null',
				)
			: null;

		this.log('constructor:restore', restore);

		if (restore?.connection) {
			// apply all data to class properties
			for (let k in restore) {
				this[k] = restore[k];
			}

			if (!restore.__public_identifier) {
				this.__public_identifier = `${this.service}:${this.owner}:${generateRandom(16)}`;
			}
		}

		// AFTER the blanket restore above, which assigns every key of a
		// same-origin-writable sessionStorage blob onto `this`. Encryption state
		// lives in a module WeakMap precisely so it cannot be injected that way.
		initEncryption.bind(this)(encryptionConfig);

		this.__authConnection = (async (): Promise<void> => {
			const admin_endpoint = await this.admin_endpoint;
			const poolSetting = {
				UserPoolId: admin_endpoint.userpool_id,
				ClientId: admin_endpoint.userpool_client,
			} as any;
			if (isNodeRuntime()) {
				poolSetting.Storage = window.localStorage;
			}
			this.userPool = new CognitoUserPool(poolSetting);

			try {
				if (!this.user) {
					// if (autoLogin && typeof autoLogin === 'object') {
					//     let { idToken = '', accessToken = '', refreshToken = '' } = autoLogin || {};
					//     if (idToken) {
					//         await this.loginWithToken({ idToken, accessToken, refreshToken });
					//     }
					// }
					await authentication.bind(this)().getSession({
						skipUserUpdateEventTrigger: true,
					});
				}
				if (this.user) {
					if (!restore?.connection && !autoLogin) {
						_out.bind(this)();
					} else {
						// A token-restored session has no password, so the only
						// way to stay unlocked across a reload is the device
						// store.
						//
						// NOT awaited here, and that is load-bearing: the unlock
						// reads the keyring with getRecords, getRecords awaits
						// __connection, and __connection awaits THIS promise.
						// Awaiting it closed the cycle and hung every call on the
						// instance. Instead the attempt is kicked off and parked
						// on the encryption state, where the record read and
						// write hooks wait for it, so an app call issued during
						// bootstrap still sees an unlocked session.
						if (encryptionConfig) {
							startDeviceUnlock.bind(this)();
						}
						// An anonymous session restored from storage: beat now, so a
						// page that was closed for a while learns at once whether its
						// account was swept (the beat signs it out then), and every
						// 10 minutes after. Not awaited, for the reason given above.
						if (this.user?.anonymous) {
							startAnonymousKeepAlive.bind(this)(true);
						}
						// only run login listeners if user is logged in (auto login successful)
						this._runOnLoginListeners(this.user);
						this._runOnUserUpdateListeners(this.user);
					}
				}
			} catch (err) {}
		})();

		// Restore only THIS instance's own scope, under a key that names both service
		// and owner. The pre-1.8.2 key (`<service>:uniqueids`) held a flat map that
		// could already have entries from several projects mixed together, so those
		// blobs are deliberately not read back: a poisoned cache is worse than none.
		// Other scopes are simply not preloaded, which costs one backend resolve.
		let uniqueIdScope = `${this.service}/${this.owner}`;
		let uniqueids = hasWindow
			? window.sessionStorage.getItem(`${uniqueIdScope}:uniqueids`)
			: null;
		if (uniqueids) {
			try {
				// sessionStorage is same-origin writable and the key is guessable, so do not
				// trust the shape. The string "null" parses without throwing, and anything
				// that is not a plain object would poison every later lookup.
				let parsed = JSON.parse(uniqueids);
				this.__my_unique_ids = (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
					? { [uniqueIdScope]: parsed }
					: {};
			} catch (err) {
				this.__my_unique_ids = {};
			}
		}

		// connects to server
		this.__connection = (async (): Promise<Connection> => {
			let connection: Promise<Connection> = null;
			await this.record_endpoint;

			if (!restore?.connection) {
				// await for first connection
				connection = this._updateConnection({refresh: refetchServiceInfo});
			}

			const storeClassProperties = () => {
				if (this.__class_properties_has_been_cached) {
					return;
				}

				let exec = () => {
					let data: Record<string, any> = {};

					const to_be_cached = [
						'__startKeyHistory', // startKey key : {}
						'__disabledAccount', // disabled account : null
						'__cached_requests', // cached records : {}
						'__request_signup_confirmation', // for resend signup confirmation : null
						'__public_identifier', // public identifier : ''
						'connection', // service info : null
					];

					if (this.connection) {
						for (let k of to_be_cached) {
							data[k] = this[k];
						}

						// Never persist the paging cache while record encryption
						// is on. On a cache hit request() returns the cached
						// response object BY REFERENCE (utils/network.ts:349),
						// and getRecords then assigns the DECRYPTED list onto
						// it, so __cached_requests ends up holding plaintext for
						// records that were encrypted precisely so they would
						// not be written to disk. Dropped as a PAIR with
						// __startKeyHistory: cursors without their cached pages
						// desync fetchMore after a restore.
						if (encryptionConfig) {
							data.__cached_requests = {};
							data.__startKeyHistory = {};
						}

						if (hasWindow) {
							const storageKey = `${service}#${owner}`;
							try {
								window.sessionStorage.setItem(
									storageKey,
									JSON.stringify(data),
								);
							} catch (err) {
								// QuotaExceededError: __cached_requests can hold
								// megabytes of paged history (indexing-pass bodies),
								// and this runs inside beforeunload/visibilitychange —
								// throwing here surfaced as an uncaught error on every
								// tab switch. The paging cache is an optimization
								// (losing it costs a refetch after reload, never
								// correctness), so retry WITHOUT the paging state —
								// dropped as a PAIR: cursors without their cached
								// pages would desync fetchMore after a restore.
								try {
									const slim = Object.assign({}, data, {
										__cached_requests: {},
										__startKeyHistory: {},
									});
									window.sessionStorage.setItem(
										storageKey,
										JSON.stringify(slim),
									);
								} catch (err2) {
									// Still over quota (something else filled it):
									// remove any STALE snapshot rather than letting a
									// previous session's state restore against this
									// one's reality.
									try { window.sessionStorage.removeItem(storageKey); } catch (_e) { /* nothing left to do */ }
								}
							}
						}
						this.__class_properties_has_been_cached = true;
					}
				};

				return connection instanceof Promise
					? connection.then(() => exec())
					: exec();
			};

			// attach event to save session on close
			if (hasWindow) {
				window.addEventListener('beforeunload', () => {
					this.closeRealtime();
					storeClassProperties();
				});
				// for mobile
				window.addEventListener('visibilitychange', () => {
					storeClassProperties();
				});
			}

			await connection;
			await this.__authConnection;
			return this.connection;
		})();

		// The .catch is not optional. This chain is fire-and-forget, so nothing
		// downstream ever attaches a handler to it, and a rejected __connection
		// (an unknown project id, or a transient failure of the service lookup)
		// therefore surfaced as an UNHANDLED REJECTION - which Node turns into a
		// process exit by default. A server that constructs a Skapi per project
		// could be killed by one bad id. There is nothing to log for a connection
		// that never resolved, so the failure is simply dropped here; every
		// caller that needs it still gets it by awaiting __connection itself.
		this.__connection
			.then((conn) => {
				if ((conn?.group || 0) < 3 || this.__network_logs) {
					this.version();
				}
			})
			.catch(() => {});
	}

	/**
	 * Returns current connection metadata such as service name, client IP, user agent, locale, and SDK version.
	 * @param params Request parameters. When `refresh` is true, the cached connection metadata is re-fetched before returning; otherwise the cached connection is returned.
	 * @returns A promise that resolves to Promise<ConnectionInfo>.
	 */
	async getConnectionInfo(params?: { refresh?: boolean }): Promise<ConnectionInfo> {
		let refresh = params?.refresh || false;
		
		if (refresh) {
			if(this.__connection instanceof Promise) {
				await this.__connection;
			}
			this.__connection = this._updateConnection({ refresh: true });
		}
		let conn = await this.__connection;
		// get browser user-agent info
		let ua =
			conn?.user_agent ||
			(isBrowserRuntime() &&
			typeof window.navigator?.userAgent === 'string'
				? window.navigator.userAgent
				: `skapi-node/${(globalThis as any)?.process?.versions?.node || 'unknown'}`);
		return {
			project_id: this.project_id,
			user_ip: conn.ip,
			user_agent: ua,
			user_location: conn.locale,
			service_name: conn.service_name,
			version: this.__version,
			service_description: conn?.service_description || "",
			ai_agent: conn?.ai_agent || "",
			conf: conn?.opt
		};
	}

	private async _updateConnection(params?: { refresh: Boolean }): Promise<Connection> {
		let refresh = params?.refresh || false;
		try {
			this.connection = await request.bind(this)(
				'service',
				{
					service: this.service,
					owner: this.owner,
					refresh
				},
				{ bypassAwaitConnection: true, method: 'get' },
			);
		} catch (err: any) {
			this.log('connection fail', err);
			this._alert(
				'Service is not available: ' + (err.message || err.toString()),
			);

			this.connection = null;
			throw err;
		}
		return this.connection;
	}

	private registerTicket = registerTicket.bind(this);
	private unregisterTicket = unregisterTicket.bind(this);
	private updateTicket = updateTicket.bind(this);

	/**
	 * Returns the current SDK version and prints runtime version details in browser environments.
	 * @returns A promise that resolves to Promise<string>.
	 */
	async version(): Promise<string> {
		await this.__connection;

		if (isBrowserRuntime()) {
			let skapi = `%c\r\n          ##\\                          ##\\ \r\n          ## |                         \\__|\r\n #######\\ ## |  ##\\ ######\\   ######\\  ##\\ \r\n##  _____|## | ##  |\\____##\\ ##  __##\\ ## |\r\n\\######\\  ######  \/ ####### |## \/  ## |## |\r\n \\____##\\ ##  _##< ##  __## |## |  ## |## |\r\n#######  |## | \\##\\\\####### |#######  |## |\r\n\\_______\/ \\__|  \\__|\\_______|##  ____\/ \\__|\r\n                             ## |          \r\n                             ## |          \r\n                             \\__|          \r\n`;
			// let skapi = `%c\r\n          $$\\                          $$\\ \r\n          $$ |                         \\__|\r\n $$$$$$$\\ $$ |  $$\\ $$$$$$\\   $$$$$$\\  $$\\ \r\n$$  _____|$$ | $$  |\\____$$\\ $$  __$$\\ $$ |\r\n\\$$$$$$\\  $$$$$$  \/ $$$$$$$ |$$ \/  $$ |$$ |\r\n \\____$$\\ $$  _$$< $$  __$$ |$$ |  $$ |$$ |\r\n$$$$$$$  |$$ | \\$$\\\\$$$$$$$ |$$$$$$$  |$$ |\r\n\\_______\/ \\__|  \\__|\\_______|$$  ____\/ \\__|\r\n                             $$ |          \r\n                             $$ |          \r\n                             \\__|          \r\n`;
			let message = `Built with:\n${skapi}Version: ${this.__version}`;
			console.log(message, `font-family: monospace; color:blue;`);
			if (this.connection.group === 1) {
				console.log(
					`%cSKAPI: THE SERVICE IS IN TRIAL MODE.`,
					`font-family: monospace; color:red;`,
				);
			}
		}
		return this.__version;
	}

	private log(n: string, v: any) {
		if (this.__network_logs) {
			if (v !== null && typeof v === 'object') {
				try {
					// if it's not a browser runtime, print as a string to avoid [Object object] issue
					if (!isBrowserRuntime()) {
						v = JSON.stringify(v, null, 2);
					} else {
						v = JSON.parse(JSON.stringify(v));
					}
				} catch (err) {
					v = String(v);
				}
			} else if (typeof v === 'string' && v.length > 100) {
				v = v.substring(0, 100) + '...';
			}

			console.log(`%c${n}:`, 'color: blue;', v);
		}
	}

	/**
	 * Fetches feed records visible to the current user with optional access-group filtering and pagination.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<RecordData>>.
	 */
	@formHandler()
	getFeed(
		params?: { access_group?: number },
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<RecordData>> {
		return getFeed.bind(this)(params, fetchOptions);
	}

	/**
	 * Closes an active WebRTC connection by cid or closes all active RTC connections.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<void>.
	 */
	@formHandler()
	closeRTC(params: { cid?: string; close_all?: boolean }): Promise<void> {
		return closeRTC.bind(this)(params);
	}

	/**
	 * Calls another user over WebRTC: the camera and microphone, the screen, a stream of your own, data channels, or any mix of them.
	 * `media` names what to send (`{ audio, video, screen }`, or a MediaStream), `receive` what to take without sending, `channels` the data channels to open,
	 * `meta` a small JSON object the other side reads on its `rtc:incoming` message, `ringTimeout` how long to wait for an answer.
	 * Resolves with an RTCConnector whose `connection` resolves, once the other side answers, with the running call: its peer connection, channels, local media,
	 * and the controls `share()`, `unshare()`, `replace()`, `mute()` and `addChannel()`.
	 * @param params Request parameters.
	 * @param callback Callback invoked for events or updates.
	 * @returns A promise that resolves to Promise<RTCConnector>.
	 */
	@formHandler()
	connectRTC(
		params: RTCConnectorParams,
		callback: (e: RTCEvent) => void,
	): Promise<RTCConnector> {
		return connectRTC.bind(this)(params, callback);
	}

	/**
	 * Opens a realtime WebSocket connection and registers a callback for incoming realtime messages.
	 * The WebSocket is delivered through the callback (and stored on the instance); on the initial connection the returned promise resolves to undefined rather than the socket.
	 * @param callback Callback invoked for events or updates.
	 * @returns A promise that resolves to Promise<WebSocket | void>.
	 */
	connectRealtime(callback: RealtimeCallback): Promise<WebSocket | void> {
		return connectRealtime.bind(this)(callback);
	}

	/**
	 * Casts a spell (uploads the given spell/name). The server response is not surfaced; resolves to a fixed confirmation string.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler()
	spellcast(params: {
		spell: string;
		name: string;
		magic?: any;
	}): Promise<string> {
		return spellcast.bind(this)(params);
	}

	/**
	 * Searches previously stored spell records by spell text or name. Defaults to 'spell' search if no search type is specified.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<DatabaseResponse<{ spell: string; magic?: any; name: string; }>>.
	 */
	@formHandler()
	getspell(params?: { search?: 'spell' | 'name'; value?: string }): Promise<
		DatabaseResponse<{
			spell: string;
			name: string;
			magic?: any;
		}>
	> {
		return getspell.bind(this)(params);
	}

	/**
	 * Uploads a dopamine message. In non-browser runtimes, resolves to a formatted string (including the video URL, and the previous message if one exists); in the browser it alerts and redirects, resolving to undefined.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string | void>.
	 */
	@formHandler()
	dopamine(params: { message: string; name: string }): Promise<string | void> {
		return dopamine.bind(this)(params);
	}
	/**
	 * Queries unique ID records by unique_id or condition filters.
	 *
	 * With `condition` omitted the unique ID is matched exactly. `gte` / `>=`
	 * is a prefix search ("unique IDs starting with x") and `lte` / `<=` is a
	 * suffix search ("unique IDs ending with x"); `gt` / `>` and `lt` / `<`
	 * are lexicographic. An 'ends with' search returns its matches in the
	 * order of the unique ID read from its last character backwards.
	 *
	 * Refused to a signed out caller when the project's `require_login`
	 * setting is on: REQUIRE_LOGIN from the SDK, INVALID_REQUEST from the
	 * backend for a caller that skips it. A project that has never set the
	 * flag counts as ON. This listing used to be served to anyone who knew
	 * the project id, and called with no `unique_id` it enumerates every
	 * unique ID in the project.
	 *
	 * The listing is NOT filtered by access group: a row is keyed by the
	 * unique ID alone, so any caller who gets past the sign in check sees the
	 * IDs of records in every group, each with the record ID it maps to.
	 * Fetching those records still goes through the usual access checks.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<UniqueId>>.
	 */
	@formHandler()
	getUniqueId(
		params?: Form<{
			/** Unique ID */
			unique_id?: string;
			/** String query condition for the unique_id value. Omitted: exact match. `gte` / `>=`: starts with. `lte` / `<=`: ends with. */
			condition?: Condition | 'ne' | '!=';
		}>,
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<UniqueId>> {
		return getUniqueId.bind(this)(params, fetchOptions);
	}

	/**
	 * Resends an existing user invitation email to the target address.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<"SUCCESS: Invitation has been re-sent. (User ID: xxx...)">.
	 */
	@formHandler()
	resendInvitation(
		params: Form<{
			email: string;
		}>,
	): Promise<'SUCCESS: Invitation has been re-sent. (User ID: xxx...)'> {
		return resendInvitation.bind(this)(params);
	}

	/**
	 * Cancels a pending invitation for the target email address.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<"SUCCESS: Invitation has been canceled.">.
	 */
	@formHandler()
	cancelInvitation(
		params: Form<{
			email: string;
		}>,
	): Promise<'SUCCESS: Invitation has been canceled.'> {
		return cancelInvitation.bind(this)(params);
	}

	/**
	 * Lists invitation records with optional email filtering and pagination.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<UserProfile>>.
	 */
	@formHandler()
	getInvitations(
		params?: Form<{
			email?: string;
		}>,
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<UserProfile>> {
		return getInvitations.bind(this)(params, fetchOptions);
	}

	/**
	 * Completes OpenID login and optionally merges the OpenID identity with an existing account.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<{ userProfile: UserProfile; openid: { [attribute: string]: string } }>.
	 */
	@formHandler()
	openIdLogin(params: {
		token: string;
		id: string;
		/**
		 * Merges this OpenID identity into the existing account whose ORIGINAL login
		 * ID (its username, or the e-mail it was created with when it has none) is
		 * this OpenID account's login ID. `true` merges; an array of OpenID attribute
		 * names also copies those attributes to the account. Merging replaces the
		 * account's password.
		 *
		 * Never merges through an e-mail login alias. openIdLogin() fails with EXISTS,
		 * with or without merge, when this OpenID account's login ID is the verified
		 * e-mail login of another account of the project (an account created with a
		 * username, or one whose e-mail was changed to it), and in the rare case the
		 * alias cannot be removed safely. Any other account holding that login ID as an
		 * e-mail login alias has not verified the e-mail, so it loses the alias instead
		 * and a new OpenID account is created for this login ID: the two accounts then
		 * share the e-mail address. The alias is removed before signup restrictions are
		 * checked, so it is gone even when the login is then refused for another reason.
		 */
		merge?: boolean | string[];
		template?: {
			/** message_id of the template to use for the welcome e-mail (sent the first time this OpenID user account is created). */
			welcome?: string;
		};
	}): Promise<{
		userProfile: UserProfile;
		openid: { [attribute: string]: string };
	}> {
		return openIdLogin.bind(this)(params);
	}

	/**
	 * Signs the visitor in with a temporary anonymous account: no e-mail, no password, access
	 * group 1, `anonymous: true` on the profile. The account lives while a page holds its
	 * session (the SDK sends a keep-alive every 10 minutes, and once when a page load restores
	 * the session) and is deleted 30 minutes after the last keep-alive, or at once on logout().
	 * It cannot edit its profile, change or reset its password, verify an e-mail or a phone
	 * number, or subscribe to newsletters. Requires the project's "Anonymous Login" setting.
	 * Resolves to the current profile when the page already holds an anonymous session, and is
	 * refused with INVALID_REQUEST while another user is logged in.
	 * @returns A promise that resolves to Promise<UserProfile>.
	 */
	anonymousLogin(): Promise<UserProfile> {
		return anonymousLogin.bind(this)();
	}

	/**
	 * Logs in a user with externally issued id/access/refresh tokens.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<UserProfile>.
	 */
	@formHandler()
	loginWithToken(params: {
		idToken: string;
		accessToken?: string;
		refreshToken?: string;
	}): Promise<UserProfile> {
		return loginWithToken.bind(this)(params);
	}

	/**
	 * Registers a named newsletter group. Service owner only.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<'SUCCESS: Group registered successfully.'>.
	 */
	@formHandler()
	registerNewsletterGroup(
		params: Form<{
			/** Name of the newsletter group. 2 ~ 20 lowercase alphanumeric characters, at least one letter, not a reserved name. */
			group: string;
			/** Access group required to subscribe. 0 ~ 99. Defaults to 0. */
			restriction?: number;
			/** Display label of the group. 60 characters max. */
			name?: string;
		}>,
	): Promise<'SUCCESS: Group registered successfully.'> {
		return registerNewsletterGroup.bind(this)(params);
	}
	/**
	 * Deletes a named newsletter group along with every subscription of that group. Service owner only.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string>, 'SUCCESS: Group has been deleted along with N subscription(s).'.
	 */
	@formHandler()
	deleteNewsletterGroup(
		params: Form<{
			/** Name of the newsletter group to delete. */
			group: string;
		}>,
	): Promise<string> {
		return deleteNewsletterGroup.bind(this)(params);
	}
	/**
	 * **Deprecated. Use `forwardRequest(form, options)` instead.**
	 *
	 * Sends a secure outbound request using a Skapi client secret key.
	 * @param params Request parameters.
	 * @returns A promise that resolves to the final API response, or to a status object when the request is queued.
	 * @deprecated Use forwardRequest(form, options), which takes the form as its first argument and makes the client secret optional. This name keeps working unchanged, and both dispatch the same request through the same code.
	 */
	@formHandler()
	clientSecretRequest(params: {
		url: string;
		clientSecretName: string;
		method: 'GET' | 'POST' | 'DELETE' | 'PUT';
		headers?: { [key: string]: string };
		data?: { [key: string]: any };
		params?: { [key: string]: string };
		poll?: number; // Polling interval in milliseconds. When > 0 the promise resolves immediately with the initial status object and the final result is delivered via onResponse/onError. When omitted or 0, the status object is returned with a poll() method to start polling manually.
		queue?: string; // Optional queue name. Requests sharing the same queue are processed sequentially.
		expires?: number; // Optional expiration time in seconds for the request record.
		onResponse?: (res: any) => void; // Called with the final API response once polling resolves, or immediately for non-queued responses.
		onError?: (err: any) => void; // Called when polling or the initial request fails.
		stream?: boolean; // Read the destination's response INCREMENTALLY and relay it as it arrives, instead of downloading it whole. Skapi relays raw bytes and never parses them, so this works against any destination. Requires a queue. NOTE: this is skapi's own parameter, and is NOT the "stream" field your destination's API may take inside `data`. Asking the destination to stream is your job, in your own body; set both together or the request fails quietly.
		onStream?: (chunk: string, seq: number) => void; // Called with each relayed piece of the response, in order, as raw text. Only meaningful with `stream: true`; its presence is what makes polling fetch the relayed pieces.
	}): Promise<any | {
		id: string;
		status: 'running' | 'pending';
		queue_name: string;
		in_queue: number;
		poll?: (arg?: {
			latency?: number;
			onStream?: (chunk: string, seq: number) => void;
		}) => Promise<any>;
	}> {
		return clientSecretRequest.bind(this)(params);
	}

	/**
	 * **Deprecated. Use `forwardRequestHistory` instead, which is this same function.**
	 *
	 * Retrieves the history of client secret requests for a given URL and method.
	 *
	 * Listing modifiers: `compact` returns lightweight label/marker stubs
	 * (request_text, response_text, response_complete_marker) instead of the
	 * full request/response bodies, which stay on the server; `queue_exact`
	 * restricts a queue listing to exactly the named queue (the queue lookup is
	 * otherwise a prefix range, so queue "u1" would also match "u1-bg");
	 * `queue_exclude` drops one queue's rows from the listing. Both queue
	 * filters are applied server-side after the range read, so a page can come
	 * back short while more matches remain - keep paging by startKey/endOfList.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to a paginated list of request history items.
	 * @deprecated Use forwardRequestHistory, which is this same function. A request started under either name is listed by both.
	 */
	@formHandler()
	clientSecretRequestHistory(
		params: {
			url: string;
			method: 'GET' | 'POST' | 'DELETE' | 'PUT';
			queue?: string; // Optional queue name to filter results.
			status?: 'pending' | 'running' | 'resolved' | 'failed'; // Optional status filter.
			compact?: boolean; // Return label/marker stubs instead of full request/response bodies.
			queue_exact?: boolean; // Match the named queue exactly (a bare queue lookup is a prefix range: "u1" also matches "u1-bg").
			queue_exclude?: string; // Drop this queue's rows from the listing.
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<RequestHistory[]>> {
		return clientSecretRequestHistory.bind(this)(params, fetchOptions);
	}

	/**
	 * **Deprecated. Use `forwardRequestStream` instead, which is this same function.**
	 *
	 * Reads a streamed clientSecretRequest that this call did not start: a page reload,
	 * a second tab, or a turn from history that was never finalized.
	 *
	 * `onStream` receives the relayed text in order, exactly as the destination sent it.
	 * skapi does not parse it; whatever grammar those bytes are in belongs to the caller
	 * and its destination. Pass `since` to resume rather than re-read from the beginning.
	 *
	 * When the request is still running this polls until it settles. When it has already
	 * settled it reads every chunk once and resolves.
	 *
	 * ```js
	 * const parser = createMyParser();
	 * await skapi.clientSecretRequestStream(requestId, {
	 *     url: 'https://api.example.com/v1/chat',
	 *     method: 'POST',
	 *     onStream: (chunk) => parser.feed(chunk)
	 * });
	 * ```
	 * @deprecated Use forwardRequestStream, which is this same function.
	 */
	clientSecretRequestStream(
		requestId: string,
		options: {
			url?: string;
			method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
			onStream?: (chunk: string, seq: number) => void;
			since?: number;
			poll?: number;
			onResponse?: (res: any) => void;
			onError?: (err: any) => void;
			service?: string;
			owner?: string;
		},
	): Promise<any> {
		return clientSecretRequestStream.bind(this)(requestId, options);
	}

	/**
	 * **Deprecated. Use `forwardRequestFinalize` instead, which is this same function.**
	 *
	 * Stores the version of a streamed response you want KEPT as history, and releases
	 * the relayed chunks it was assembled from.
	 *
	 * The content is entirely yours. skapi neither validates nor interprets it: it stores
	 * what you tell it to store. Only the caller who made the request may finalize it.
	 *
	 * Until you finalize, the chunks stay indefinitely and every read of that turn has to
	 * fetch and re-parse all of them, so finalize as soon as you have rendered the answer.
	 *
	 * ```js
	 * await skapi.clientSecretRequestFinalize(requestId, parser.result(), {
	 *     url: 'https://api.example.com/v1/chat',
	 *     method: 'POST'
	 * });
	 * ```
	 * @deprecated Use forwardRequestFinalize, which is this same function.
	 */
	clientSecretRequestFinalize(
		requestId: string,
		data?: any,
		options?: {
			url?: string;
			method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
			service?: string;
			owner?: string;
		},
	): Promise<{ finalized: boolean; message: string }> {
		return clientSecretRequestFinalize.bind(this)(requestId, data, options);
	}

	/**
	 * **Deprecated. Use `cancelForwardRequest` instead, which is this same function.**
	 *
	 * Cancels a pending client secret request and removes it from the client-side queue if applicable.
	 * @param params Request parameters.
	 * @returns A promise that resolves to a result object with removed status and message.
	 * @deprecated Use cancelForwardRequest, which is this same function.
	 */
	@formHandler()
	cancelClientSecretRequest(params: {
		url: string;
		method: 'GET' | 'POST' | 'DELETE' | 'PUT';
		id: string; // Request ID to cancel.
		queue?: string; // Optional queue name the request belongs to, used to remove it from the client-side queue.
	}): Promise<{ removed: boolean; message: string }> {
		return cancelClientSecretRequest.bind(this)(params);
	}

	/**
	 * **Deprecated. Use `stopForwardRequestPolling` instead, which is this same function.**
	 *
	 * Stops live polling for client secret requests without cancelling the requests
	 * themselves. The server-side work continues; only this client stops asking about it.
	 *
	 * Use it to drop polling traffic while the user is not looking at the results (a
	 * hidden tab, a closed view), then simply poll again when they return. Polls run
	 * one-at-a-time per queue, so a poll for a request that never settles otherwise
	 * blocks every poll queued behind it.
	 *
	 * Pass `id` (with `url` and `method`) to stop one request, `queue` to stop a queue's
	 * polls, or neither to stop all of them. A stopped poll resolves with
	 * `{ id, status: 'stopped' }` rather than rejecting, and its `onResponse`/`onError`
	 * callbacks are not called.
	 *
	 * @param params Which polls to stop.
	 * @returns The number of polls stopped.
	 * @deprecated Use stopForwardRequestPolling, which is this same function.
	 */
	stopClientSecretPolling(params?: {
		url?: string;
		method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
		id?: string; // Request ID (short form, paired with url+method, or an already-full id).
		queue?: string; // Queue name the polls were started with.
		service?: string;
		owner?: string;
	}): number {
		return stopClientSecretPolling.bind(this)(params || {});
	}

	/**
	 * True if a poll result came from stopForwardRequestPolling (or its deprecated alias
	 * stopClientSecretPolling) rather than the server.
	 * @param res A resolved poll result.
	 */
	isPollStopped(res: any): boolean {
		return isPollStopped(res);
	}

	/**
	 * **Deprecated. Use `forwardRequestQueueCount` instead, which is this same function.**
	 *
	 * Returns the number of requests currently waiting in a named client secret request queue.
	 * @param params Request parameters.
	 * @returns A promise that resolves to queue count information.
	 * @deprecated Use forwardRequestQueueCount, which is this same function.
	 */
	clientSecretRequestQueueCount(params: {
		queue: string;
		service?: string;
		owner?: string;
	}): Promise<{ queue_name: string; in_queue: number }> {
		return clientSecretRequestQueueCount.bind(this)(params);
	}

	/* --------------------------------------------------------------- *
	 * The forwardRequest family.
	 *
	 * Each of these IS the method above it: the imported name is an alias in
	 * methods/request, not a second implementation, so there is nothing here that
	 * could drift. A request dispatched under either name is read, cancelled,
	 * finalized and counted by both.
	 * --------------------------------------------------------------- */

	/**
	 * Lists forwarded requests for a given url and method, newest first.
	 *
	 * Listing modifiers: `compact` returns lightweight label/marker stubs
	 * (request_text, response_text, response_complete_marker) instead of the
	 * full request/response bodies, which stay on the server; `queue_exact`
	 * restricts a queue listing to exactly the named queue (the queue lookup is
	 * otherwise a prefix range, so queue "u1" would also match "u1-bg");
	 * `queue_exclude` drops one queue's rows from the listing. Both queue
	 * filters are applied server-side after the range read, so a page can come
	 * back short while more matches remain - keep paging by startKey/endOfList.
	 *
	 * The same function as the deprecated clientSecretRequestHistory, so a request
	 * sent under either name is listed here.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to a paginated list of request history items.
	 */
	@formHandler()
	forwardRequestHistory(
		params: {
			url: string;
			method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
			queue?: string; // Optional queue name to filter results.
			status?: 'pending' | 'running' | 'resolved' | 'failed'; // Optional status filter.
			compact?: boolean; // Return label/marker stubs instead of full request/response bodies.
			queue_exact?: boolean; // Match the named queue exactly (a bare queue lookup is a prefix range: "u1" also matches "u1-bg").
			queue_exclude?: string; // Drop this queue's rows from the listing.
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<RequestHistory[]>> {
		return forwardRequestHistory.bind(this)(params, fetchOptions);
	}

	/**
	 * Reads a streamed forwardRequest that this call did not start: a page reload,
	 * a second tab, or a turn from history that was never finalized.
	 *
	 * `onStream` receives the relayed text in order, exactly as the destination sent it.
	 * skapi does not parse it; whatever grammar those bytes are in belongs to the caller
	 * and its destination. Pass `since` to resume rather than re-read from the beginning.
	 *
	 * When the request is still running this polls until it settles. When it has already
	 * settled it reads every chunk once and resolves.
	 *
	 * ```js
	 * const parser = createMyParser();
	 * await skapi.forwardRequestStream(requestId, {
	 *     url: 'https://api.example.com/v1/chat',
	 *     method: 'POST',
	 *     onStream: (chunk) => parser.feed(chunk)
	 * });
	 * ```
	 */
	forwardRequestStream(
		requestId: string,
		options: {
			url?: string;
			method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
			onStream?: (chunk: string, seq: number, via?: 'socket' | 'poll') => void;
			since?: number;
			poll?: number;
			realtimeGroup?: string;
			onResponse?: (res: any) => void;
			onError?: (err: any) => void;
			service?: string;
			owner?: string;
		},
	): Promise<any> {
		return forwardRequestStream.bind(this)(requestId, options);
	}

	/**
	 * Stores the version of a streamed response you want KEPT as history, and releases
	 * the relayed chunks it was assembled from.
	 *
	 * The content is entirely yours. skapi neither validates nor interprets it: it stores
	 * what you tell it to store. Only the caller who made the request may finalize it.
	 *
	 * Until you finalize, the chunks stay indefinitely and every read of that turn has to
	 * fetch and re-parse all of them, so finalize as soon as you have rendered the answer.
	 *
	 * ```js
	 * await skapi.forwardRequestFinalize(requestId, parser.result(), {
	 *     url: 'https://api.example.com/v1/chat',
	 *     method: 'POST'
	 * });
	 * ```
	 */
	forwardRequestFinalize(
		requestId: string,
		data?: any,
		options?: {
			url?: string;
			method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
			service?: string;
			owner?: string;
		},
	): Promise<{ finalized: boolean; message: string }> {
		return forwardRequestFinalize.bind(this)(requestId, data, options);
	}

	/**
	 * Cancels a queued forwarded request and removes it from the client-side queue if applicable.
	 * @param params Request parameters.
	 * @returns A promise that resolves to a result object with removed status and message.
	 */
	@formHandler()
	cancelForwardRequest(params: {
		url: string;
		method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
		id: string; // Request ID to cancel.
		queue?: string; // Optional queue name the request belongs to, used to remove it from the client-side queue.
	}): Promise<{ removed: boolean; message: string }> {
		return cancelForwardRequest.bind(this)(params);
	}

	/**
	 * Stops live polling of forwarded requests without cancelling the requests
	 * themselves. The server-side work continues; only this client stops asking about it.
	 *
	 * Use it to drop polling traffic while the user is not looking at the results (a
	 * hidden tab, a closed view), then simply poll again when they return. Polls run
	 * one-at-a-time per queue, so a poll for a request that never settles otherwise
	 * blocks every poll queued behind it.
	 *
	 * Pass `id` (with `url` and `method`) to stop one request, `queue` to stop a queue's
	 * polls, or neither to stop all of them. A stopped poll resolves with
	 * `{ id, status: 'stopped' }` rather than rejecting, and its `onResponse`/`onError`
	 * callbacks are not called.
	 *
	 * @param params Which polls to stop.
	 * @returns The number of polls stopped.
	 */
	stopForwardRequestPolling(params?: {
		url?: string;
		method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
		id?: string; // Request ID (short form, paired with url+method, or an already-full id).
		queue?: string; // Queue name the polls were started with.
		service?: string;
		owner?: string;
	}): number {
		return stopForwardRequestPolling.bind(this)(params || {});
	}

	/**
	 * Returns the number of requests currently waiting in a named forward request queue.
	 * @param params Request parameters.
	 * @returns A promise that resolves to queue count information.
	 */
	forwardRequestQueueCount(params: {
		queue: string;
		service?: string;
		owner?: string;
	}): Promise<{ queue_name: string; in_queue: number }> {
		return forwardRequestQueueCount.bind(this)(params);
	}

	/**
	 * Consumes a ticket: calls its endpoint with `data`, so the ticket's condition is checked and
	 * its actions run. An anonymous POST goes to `/tp/`, an anonymous GET to `/tg/` with `data`
	 * as the query string, and a signed-in consumption (`auth: true`) to `/tpa/`, which is POST only.
	 *
	 * Resolves with the receipt (TicketReceipt: ticket_id, consume_id, user_id, is_test, timestamp, hash),
	 * or with the body a Respond action of the ticket answered, as sent and whatever its status. `T` names that body.
	 *
	 * A failed consumption rejects with a SkapiError: `err.code` is the ticket error code and
	 * `err.cause` the flat TicketError body (`err.cause.stage`, `err.cause.action`, `err.cause.detail`).
	 * This holds when the ticket answers HTTP 200 as well (`return200`): the body is inspected, not the status.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<T>, the receipt or the Respond body.
	 */
	@formHandler()
	consumeTicket<T = TicketReceipt>(params: {
		/** ID of the ticket to consume. */
		ticket_id: string;
		/** "GET" or "POST". */
		method: 'GET' | 'POST';
		/** Consume as the signed-in user. POST only. */
		auth?: boolean;
		/** The POST body, or the query string of a GET. */
		data?: {
			[key: string]: any;
		};
	}): Promise<T> {
		// bind() drops the generic, so the receipt type it infers is widened to T here
		return consumeTicket.bind(this)(params) as unknown as Promise<T>;
	}

	/**
	 * Lists the tickets the signed-in user has consumed, with optional filters and pagination.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<any>>.
	 */
	@formHandler()
	getConsumedTickets(
		params: {
			/** Only the consumptions of this ticket. */
			ticket_id?: string;
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<any>> {
		return getConsumedTickets.bind(this)(params, fetchOptions);
	}

	/**
	 * Lists issued tickets with optional filters and pagination.
	 * The project owner gets the full ticket (condition, actions, count, expiry, per-user limit); any other signed-in user gets `ticket_id`, `description`, `count`, `time_to_live` and `timestamp` only.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<any>>.
	 */
	@formHandler()
	getTickets(
		params: {
			/** Absent = every ticket. "<id>" = that ticket. "#<id>#" = the consumption log of that ticket, "@<id>#<consume id>#" the action rows of one consumption (project owner only). */
			ticket_id?: string;
			/** A log listing only: rows from this time on, in milliseconds since the epoch. */
			from?: number;
			/** A log listing only: rows up to this time, in milliseconds since the epoch. */
			to?: number;
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<any>> {
		return getTickets.bind(this)(params, fetchOptions);
	}

	/**
	 * Deletes a ticket's log rows before a time, in the background over the next minutes. The ticket's
	 * remaining count and its per-user limits are unchanged. Project owner only.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<{ message: string; ticket_id: string; before: number }>, `before` being the time applied (later than now is clipped to now).
	 */
	@formHandler()
	clearTicketLog(params: {
		/** The ticket whose log rows go. */
		ticket_id: string;
		/** Rows before this time, in milliseconds since the epoch. Default: now. */
		before?: number;
	}): Promise<{ message: string; ticket_id: string; before: number }> {
		return clearTicketLog.bind(this)(params);
	}

	/**
	 * Closes the active realtime WebSocket connection.
	 * @returns A promise that resolves to Promise<void>.
	 */
	closeRealtime(): Promise<void> {
		return closeRealtime.bind(this)();
	}

	/**
	 * Fetches currently connected users in a realtime group.
	 * @param params Request parameters. When `group` is omitted, it defaults to the realtime group the user is currently joined to.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<{ user_id: string; cid: string }[]>>.
	 */
	@formHandler()
	getRealtimeUsers(
		params?: { group?: string; user_id?: string },
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<{ user_id: string; cid: string }[]>> {
		return getRealtimeUsers.bind(this)(params, fetchOptions);
	}

	/**
	 * Sends an inquiry email payload to the service contact channel.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<"SUCCESS: Inquiry has been sent.">.
	 */
	@formHandler()
	sendInquiry(
		params: Form<{
			name: string;
			email: string;
			subject: string;
			message: string;
		}>,
	): Promise<'SUCCESS: Inquiry has been sent.'> {
		return sendInquiry.bind(this)(params);
	}

	/**
	 * Blocks a user account by user_id.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<"SUCCESS: The user has been blocked.">.
	 */
	@formHandler()
	blockAccount(params: {
		user_id: string;
	}): Promise<'SUCCESS: The user has been blocked.'> {
		return blockAccount.bind(this)(params);
	}

	/**
	 * Unblocks a previously blocked user account by user_id.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<"SUCCESS: The user has been unblocked.">.
	 */
	@formHandler()
	unblockAccount(params: {
		user_id: string;
	}): Promise<'SUCCESS: The user has been unblocked.'> {
		return unblockAccount.bind(this)(params);
	}

	/**
	 * Deletes a user account by user_id.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<"SUCCESS: Account has been deleted.">.
	 */
	@formHandler()
	deleteAccount(params: {
		user_id: string;
	}): Promise<'SUCCESS: Account has been deleted.'> {
		return deleteAccount.bind(this)(params);
	}

	/**
	 * Invites a user by e-mail. The invitation carries a temporary password and a
	 * link to accept, valid for 7 days. Admin only (access_group 90 and above).
	 *
	 * Every profile attribute passed in `params` is written to the account when the
	 * invitation is sent and is kept unchanged when the user accepts; accepting only
	 * activates the account and marks its e-mail verified.
	 *
	 * Refused with EXISTS when the e-mail, or the username when one is given, is
	 * already another account's login ID, and with EXISTS "User is already
	 * invited." while the e-mail has a pending invitation.
	 * @param params The invited user's e-mail, and optionally their profile.
	 * @param options Where to send them after accepting, newsletter opt-in, and a custom template.
	 * @returns A promise that resolves to Promise<'SUCCESS: Invitation has been sent. (User ID: xxx...)'>.
	 */
	@formHandler()
	inviteUser(
		params: UserAttributes & {
			/** Required. The invitation, with the temporary password and the link to accept, is sent here. */
			email: string
			/**
			 * Optional. Becomes the invited account's PERMANENT login username and
			 * can never be changed. The e-mail also logs the account in once the
			 * invitation is accepted (best effort), not while it is pending. Refused
			 * with EXISTS when the username or the e-mail is already another
			 * account's login ID.
			 */
			username?: string
			/** ID of an OpenID logger registered in the project, to link the invited account to it. */
			openid_id?: string
			/** 1~99. Defaults to 1. 99 is admin level. */
			access_group?: number
		},
		options?: {
			/** URL the user is taken to after accepting. Must not contain "#". */
			confirmation_url?: string;
			/**
			 * Subscribe the user to Service Email (group 1) once they accept.
			 * Requires `confirmation_url`. Defaults to false.
			 */
			email_subscription?: boolean;
			/** A custom HTML template for this invitation e-mail. Both fields are required. */
			template?: {
				/** URL of the HTML template. Must include the required invitation placeholders. */
				url: string;
				/** Subject line of the e-mail. */
				subject: string;
			};
		},
	): Promise<'SUCCESS: Invitation has been sent. (User ID: xxx...)'> {
		return inviteUser.bind(this)(params, options);
	}

	/**
	 * Creates a user account directly from admin context.
	 *
	 * Refused with EXISTS when the e-mail, or the username when one is given, is
	 * already another account's login ID.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<UserProfile & { email_admin: string; username: string; }>.
	 */
	@formHandler()
	createAccount(
		params: UserAttributes & {
			/** Required. Always. */
			email: string
			password: string
			/**
			 * Optional. Becomes the account's PERMANENT login username, which
			 * always logs the account in. The e-mail is created unverified, so it
			 * logs the account in only once the user verifies it with verifyEmail()
			 * after logging in with the username. Refused with EXISTS when the
			 * username or the e-mail is already another account's login ID.
			 */
			username?: string
			access_group?: number
		},
	): Promise<UserProfile & { email_admin: string; username: string }> {
		return createAccount.bind(this)(params);
	}

	/**
	 * Updates a user's access_group level.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<'SUCCESS: Access has been granted to the user.'>.
	 */
	@formHandler()
	grantAccess(params: {
		user_id: string;
		access_group: number;
	}): Promise<'SUCCESS: Access has been granted to the user.'> {
		return grantAccess.bind(this)(params);
	}

	/**
	 * Updates another user's profile attributes from admin context.
	 * Requires the target user's user_id plus at least one attribute to update.
	 *
	 * Never the caller's own account, the project owner and access group 99 admins
	 * included: the caller's own user_id is refused with INVALID_REQUEST and
	 * 'Cannot modify attributes of the current user.' before any request is sent.
	 * Change your own profile with updateProfile() without user_id.
	 *
	 * An admin in access groups 90 ~ 98 cannot update an account whose access group
	 * is at or above their own, a disabled account included (it counts at the group
	 * it had): refused with INVALID_REQUEST and 'No access to modify admin.', and
	 * nothing is written. Access group 99 admins and the project owner are not limited.
	 *
	 * A changed e-mail is written unverified: this method never marks an e-mail
	 * verified, for an admin in access groups 90 ~ 98 or anyone else. The new e-mail
	 * logs the account in only once the user verifies it with verifyEmail(). The
	 * previous e-mail stops logging in when the change is written, unless it is the
	 * e-mail an account without a username was created with, which stays its login
	 * ID. A username always logs in. The change is refused with EXISTS and
	 * 'E-mail "user@email.com" is already a login ID in this service.' when the
	 * e-mail is a login ID another account of the project was granted: the address
	 * that account was created or invited with, or one it has verified. An e-mail
	 * login another account holds without having verified the address blocks
	 * nothing; it is removed, and that account keeps the login ID it was created
	 * with.
	 * @param params Target user_id and the attributes to update. Not the `*_public` visibility flags: they are set when the account is created and are the user's own afterwards; the backend refuses them from an admin.
	 * @returns A promise that resolves to Promise<'SUCCESS: User attributes updated.'>.
	 */
	@formHandler()
	updateUserAttributes(
		params: Omit<UserAttributes, UserVisibilityFlag> & { user_id: string },
	): Promise<'SUCCESS: User attributes updated.'> {
		return updateUserAttributes.bind(this)(params);
	}

	/**
	 * Lists realtime groups with optional search conditions and pagination.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<{ group: string; number_of_users: number; }>>.
	 */
	@formHandler()
	getRealtimeGroups(
		params?: {
			/** Index name to search. */
			searchFor?: 'group' | 'number_of_users';
			/** Index value to search. */
			value?: string | number;
			/** Search condition. */
			condition?:
				| '>'
				| '>='
				| '='
				| '<'
				| '<='
				| '!='
				| 'gt'
				| 'gte'
				| 'eq'
				| 'lt'
				| 'lte'
				| 'ne';
			/** Range of search. */
			range?: string | number;
		} | null,
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<{ group: string; number_of_users: number }>> {
		return getRealtimeGroups.bind(this)(params, fetchOptions);
	}
	/**
	 * Lists every named newsletter group of the service with its restriction, subscriber count and sending address. Service owner only.
	 * @returns A promise that resolves to Promise<{ groups: NewsletterGroup[] }>.
	 */
	@formHandler()
	newsletterGroupEndpoint(): Promise<{ groups: NewsletterGroup[] }> {
		return newsletterGroupEndpoint.bind(this)();
	}
	/**
	 * Sends realtime data to a user or group with optional push notification metadata.
	 * @param message Message payload to send.
	 * @param recipient Recipient user or group target.
	 * @param notification Optional notification payload.
	 * @returns A promise that resolves to Promise<{ type: 'success', message: 'Message sent.' }>.
	 */
	@formHandler()
	postRealtime(
		message: any,
		recipient: string,
		notification?: {
			config?: { always: boolean };
			title: string;
			body: string;
		},
	): Promise<{ type: 'success'; message: 'Message sent.' }> {
		return postRealtime.bind(this)(message, recipient, notification);
	}

	/**
	 * Joins the current connection to a realtime group or leaves group membership when null.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<{ type: 'success', message: string }>.
	 */
	@formHandler()
	joinRealtime(params: {
		group?: string | null;
	}): Promise<{ type: 'success'; message: string }> {
		return joinRealtime.bind(this)(params);
	}

	// /**
	//  * Returns the active service connection object after initialization.
	//  * @returns A promise that resolves to Promise<Connection>.
	//  */
	// private _getConnection(): Promise<Connection> {
	//     return this.__connection;
	// }

	/**
	 * Retrieves the currently authenticated user profile, optionally refreshing token/session state.
	 * @param options Optional behavior configuration.
	 * @returns A promise that resolves to Promise<UserProfile | null>.
	 */
	@formHandler()
	getProfile(options?: {
		refreshToken?: boolean;
	}): Promise<UserProfile | null> {
		return getProfile.bind(this)(options);
	}
	/**
	 * Retrieves a file by URL with optional conversion mode, expiration, and progress handling.
	 * @param url Target URL.
	 * @param config Additional configuration options.
	 * @returns A promise that resolves to Promise<Blob | string | void | FileInfo>.
	 */
	@formHandler()
	getFile(
		url: string, // cdn endpoint url https://xxxx.cloudfront.net/path/file
		config?: {
			dataType?:
				| 'base64'
				| 'download'
				| 'endpoint'
				| 'blob'
				| 'text'
				| 'info'; // default 'download'
			expires?: number; // uses url that expires. this option does not use the cdn (slow). can be used for private files. (does not work on public files).
			// Seconds the browser may reuse the url minted for `expires`. Without
			// it every call mints a new signed url, which is a new cache key, so
			// the file downloads again every time. See getFile in
			// methods/database.ts. Only meaningful together with `expires`.
			browserCache?: number;
			// Bypass the cached mint above and force a fresh signed url.
			refresh?: boolean;
			progress?: ProgressCallback;
		},
	): Promise<Blob | string | void | FileInfo> {
		return getFile.bind(this)(url, config);
	}
	/**
	 * Executes authenticated secure API requests in single or batch mode.
	 * @param params Request parameters.
	 * @param url Target URL.
	 * @returns A promise that resolves to Promise<Response | Response[]>.
	 */
	@formHandler()
	secureRequest<
		Params = {
			/** Request url */
			url: string;
			/** Request data */
			data?: any;
			/** requests are sync when true */
			sync?: boolean;
		},
		Response = { response: any; statusCode: number; url: string },
	>(
		params: Params[] | Form<Params>,
		url?: string,
	): Promise<Response | Response[]> {
		return secureRequest.bind(this)(params, url);
	}
	/**
	 * Relays a request to a destination of your choosing from the server side,
	 * optionally with one of your stored client secrets substituted in where you
	 * put "$CLIENT_SECRET", so the secret never reaches the browser.
	 *
	 * The first argument is the form: a submit event, a form element, FormData, a
	 * plain object, or null when everything is already in the options. It is
	 * FLATTENED to fields and merged into the request, into `params` for GET,
	 * DELETE and HEAD and into `data` otherwise; where a key is in both, the one
	 * you typed in options wins. Files are dropped by that flattening, because
	 * the merged object travels as JSON. Pass `multipart: true` to relay the
	 * form's own bytes instead, files included, the way a browser would send
	 * them; keep that body under 2 MB once encoded, and `data` is refused
	 * alongside it.
	 *
	 * `secretName` is optional: name one and its value is substituted server side
	 * and its allowed destinations are enforced, name none and only what you
	 * supplied is forwarded. `skapiHeaders` tells the destination who is calling,
	 * from the verified identity of the request; a header of your own starting
	 * with "x-skapi-" is refused, which is what makes that prefix worth trusting.
	 *
	 * Queueing, polling, `stream`, `realtime` and the poll handle on the reply all
	 * behave exactly as they do for the deprecated clientSecretRequest, because
	 * they are the same code.
	 *
	 * @param form Submit event, form element, FormData, plain object, or null for no form body.
	 * @param options Destination url and method, the optional secret name, and how to read the answer.
	 * @returns A promise that resolves to the destination's response, or to a status object when the request is queued.
	 */
	// Decorated like every other form-taking method, so
	// onsubmit="skapi.forwardRequest(event, {...})" works with no preventDefault
	// from the caller: the decorator calls it and passes the event through
	// untouched.
	//
	// One caveat belongs with a STREAMING call. When the form carries an `action`
	// attribute the decorator serializes the resolved value into sessionStorage
	// and navigates there, which throws the stream away. Streaming forms should
	// have no action.
	@formHandler()
	forwardRequest(
		form: SubmitEvent | HTMLFormElement | FormData | { [key: string]: any } | null,
		options: {
			secretName?: string; // Stored client secret to substitute for "$CLIENT_SECRET". Optional.
			url: string;
			method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD'; // Defaults to POST.
			headers?: { [key: string]: string }; // A name starting with "x-skapi-" is refused.
			data?: { [key: string]: any }; // Wins over the form on a key collision. Refused with multipart.
			params?: { [key: string]: string }; // Query string, and the merge target for GET, DELETE and HEAD.
			multipart?: boolean; // Relay the form's own bytes, files included, instead of flattening it to fields.
			skapiHeaders?: boolean | { user?: boolean; service?: boolean }; // Send "x-skapi-user" and/or "x-skapi-service", written server side from the verified identity.
			service?: string; // The service to run the request against when it is not this instance's own, for an admin acting on a user's project. Give "owner" with it.
			owner?: string; // The owner of "service".
			poll?: number; // Polling interval in milliseconds. When > 0 the promise resolves with the polled result; when omitted or 0, the status object carries a poll() to start polling manually.
			queue?: string; // Optional queue name. Requests sharing the same queue are processed sequentially.
			expires?: number; // Optional expiration time in seconds for the request record.
			stream?: boolean; // Read the destination's response INCREMENTALLY and relay it as it arrives. Requires a queue, and one is minted when you do not name it. NOTE: this is skapi's own parameter, and is NOT the "stream" field your destination's API may take inside `data`.
			realtime?: boolean; // Also push each relayed chunk over skapi's websocket. Requires stream: true; an accelerator only.
			onStream?: (chunk: string, seq: number, via?: 'socket' | 'poll') => void; // Called with each relayed piece of the response, in order, as raw text.
			onResponse?: (res: any, meta?: { executed?: number }) => void; // Called with the final response once polling resolves, or immediately for non-queued responses.
			onError?: (err: any) => void; // Called when polling or the initial request fails.
			responseType?: 'json' | 'text' | 'response'; // How to resolve with the destination's answer. A skapi status object is handed back untouched.
			signal?: AbortSignal; // Aborting stops THIS CLIENT polling. The queued request is not cancelled; cancelForwardRequest removes it.
		},
	): Promise<any> {
		return forwardRequest.bind(this)(form, options);
	}
	/**
	 * Returns a normalized response object for form-based handler flows.
	 * @returns A promise that resolves to Promise<any>.
	 */
	@formHandler()
	getFormResponse(): Promise<any> {
		return getFormResponse.bind(this)();
	}
	/**
	 * Queries database records with filtering, sorting, and pagination options.
	 * @param query Query object used to filter results.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<RecordData>>.
	 */
	@formHandler()
	getRecords(
		query: GetRecordQuery & { private_key?: string },
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<RecordData>> {
		return getRecords.bind(this)(query, fetchOptions);
	}
	/**
	 * Lists table metadata with optional table-name filters.
	 *
	 * With `table` given and `condition` omitted, the table name is matched
	 * exactly. With `table` omitted entirely, every table is returned.
	 * `gte` / `>=` is a prefix search ("table names starting with x"), while
	 * `gt` / `>` is a lexicographic greater-than that spills past the prefix
	 * into every later table name. `lte` / `<=` is a suffix search ("table
	 * names ending with x"); its matches come back in the order of the name
	 * read from its last character backwards.
	 *
	 * Every combination:
	 * - `getTables({})`: every table in the project.
	 * - `getTables({ table: 'x' })`: exact match on 'x'.
	 * - `getTables({ table: 'x', condition: 'gte' })`: prefix, every table
	 *   name starting with 'x'.
	 * - `getTables({ table: 'x', condition: 'lte' })`: suffix, every table
	 *   name ending with 'x'.
	 * - `getTables({ table: '' })`: errors with '"table" should not be empty.'
	 * - `getTables({ condition: 'gte' })` with no `table`: errors with
	 *   '"table" is required for condition.'
	 *
	 * When you are looking for a table rather than listing them all, pass
	 * `gte`: a name recorded two ways, such as "Asian Spice House" and "Asian
	 * Spice House (alias)", is only found by the prefix. Real table names
	 * very often carry a leading name shared with the rest of their data set,
	 * a source or dataset prefix, so an exact match finds one spelling and
	 * silently misses its siblings while the prefix finds the family. When
	 * you already know the exact name, omitting `condition` is the exact
	 * match you want. Omit the key to get the default: an explicit undefined
	 * or null `condition` is rejected with INVALID_PARAMETER.
	 *
	 * Refused to a signed out caller when the project's `require_login`
	 * setting is on. The SDK throws REQUIRE_LOGIN before the request leaves,
	 * and the backend refuses the same call with INVALID_REQUEST, so a direct
	 * HTTP call or an older SDK with no gate is refused too. A project that
	 * has never set the flag counts as ON, the same default
	 * `getConnectionInfo()` already reports as `conf.require_login`. Table
	 * metadata used to be served to anyone who knew the project id, so an
	 * integration that listed tables with no signed in user now gets an error
	 * where it used to get a list.
	 *
	 * Table NAMES are never filtered: every table in the project is listed,
	 * whatever access group the records inside it live in. The per access
	 * group counters are filtered to the caller.
	 * `number_of_records_in_access_group_public` goes to everyone, a signed in
	 * user also receives the counters up to and including their own access
	 * group, and `number_of_records_in_access_group_private` and
	 * `number_of_records_in_access_group_admin` go to an admin or the project
	 * owner only. `number_of_records` and `size` are NOT filtered: both stay
	 * totals over every access group, so `number_of_records` is normally
	 * larger than the counters you can see add up to.
	 * @param query Query object used to filter results.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<Table>>.
	 */
	@formHandler()
	getTables(
		/** If omitted, fetches the full list of tables. */
		query?: {
			table?: string;
			/** Condition operator of table name. Omitted: exact match on the given table name. `gte` / `>=`: starts with. `lte` / `<=`: ends with. */
			condition?: Condition;
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<Table>> {
		return getTables.bind(this)(query, fetchOptions);
	}
	/**
	 * Lists index metadata and aggregated index statistics for a table.
	 *
	 * There is no top-level `condition` here. With no `index` and no `order`,
	 * every index of the table is listed, which is a prefix read. An `index`
	 * without a trailing '.' is an exact match on that index name; an `index`
	 * ending in '.' is a prefix that lists the children of that compound
	 * index, so "Band." lists Band.name, Band.year.
	 *
	 * The only condition is `order.condition`, which requires `order.value`.
	 * Omitting it matches `order.value` exactly.
	 *
	 * With `order.by: 'index_name'`, `order.value` is a piece of the name and
	 * the condition searches the names: `>=` 'starts with', `<=` 'ends with',
	 * `>` and `<` the names after and before it in order. 'Ends with' matches
	 * the name segment right under `index` (or the first segment when `index`
	 * is omitted), and returns what is nested under a match as well, the way
	 * 'starts with' does: `index: 'Band.'` with `value: 'ers'` and `<=` finds
	 * Band.Members, Band.Members.name and Band.Singers.
	 *
	 * Every combination (`table` is required, so there is no no-argument
	 * form):
	 * - `getIndexes({ table: 't' })`: every index of table 't'.
	 * - `getIndexes({ table: 't', index: 'Band' })`: exact match on the index
	 *   'Band'.
	 * - `getIndexes({ table: 't', index: 'Band.' })`: prefix, the children of
	 *   the compound index, so Band.name, Band.year.
	 * - `getIndexes({ table: 't', order: { by: 'index_name', value: 'B' } })`:
	 *   exact match against the value.
	 * - `getIndexes({ table: 't', order: { by: 'index_name' } })`: every index
	 *   of the table, ordered by name.
	 * - `getIndexes({ table: 't', order: { by: 'index_name', value: 'B', condition: '>=' } })`:
	 *   the index names that start with 'B'.
	 * - `getIndexes({ table: 't', order: { by: 'index_name', value: 'e', condition: '<=' } })`:
	 *   the index names that end with 'e'.
	 * - `getIndexes({ table: 't', order: { by: 'total_number' } })`: the whole
	 *   partition, ordered by that attribute.
	 * - `order.condition` without `order.value`: errors.
	 * - no `table`: errors with '"table" is required.'
	 *
	 * When you are looking for an index rather than listing them all, use the
	 * prefix form (no `index`, or a name ending in '.') the way `gte` explores
	 * in getTables and getTags, since an exact `index` name returns only that
	 * one entry. Index names very often carry a leading name shared with the
	 * rest of their data set, so an exact match finds one spelling and
	 * silently misses its siblings while the prefix finds the family. Omit the
	 * key to get the default: an explicit undefined or null condition is
	 * rejected with INVALID_PARAMETER.
	 *
	 * Refused to a signed out caller when the project's `require_login`
	 * setting is on: REQUIRE_LOGIN from the SDK, INVALID_REQUEST from the
	 * backend for a caller that skips it. A project that has never set the
	 * flag counts as ON. Index metadata used to be served to anyone who knew
	 * the project id.
	 *
	 * The listing is NOT filtered by access group, for any caller. A stored
	 * index row is keyed by table and index name with the access group left
	 * out of the key, so one row aggregates every group: `number_of_records`,
	 * `total_number`, `average_number` and the rest span the whole table,
	 * private and admin records included. Anyone who gets past the sign in
	 * check therefore reads the project's entire index vocabulary. The
	 * records behind it stay gated, but an index name is visible to every
	 * signed in user.
	 * @param query Query object used to filter results.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<Index>>.
	 */
	@formHandler()
	getIndexes(
		query: {
			/** Table name */
			table: string;
			/** Index name. When period is at the end of name, querys nested index keys. */
			index?: string;
			/** Queries order by */
			order?: {
				/** Key name to order by. */
				by:
					| 'average_number'
					| 'total_number'
					| 'number_count'
					| 'average_bool'
					| 'total_bool'
					| 'bool_count'
					| 'string_count'
					| 'index_name'
					| 'number_of_records';
				/** Value to query. A string when "by" is 'index_name': a piece of the index name. */
				value?: number | boolean | string;
				/** Requires "value". Omitted: exact match against "value". With "by" 'index_name': `>=` starts with, `<=` ends with. */
				condition?: Condition;
			};
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<Index>> {
		return getIndexes.bind(this)(query, fetchOptions);
	}
	/**
	 * Lists tags used in a table with optional tag-name filtering.
	 *
	 * With `condition` omitted the default depends on what else you gave:
	 * `table` and `tag` together match the tag exactly, `table` alone is a
	 * prefix ('>=') that lists every tag in that table, and neither one
	 * returns every tag in the project, ordered by record count, descending.
	 * `gte` / `>=` is a prefix search and `lte` / `<=` is a suffix search:
	 * with `table` it finds the tags of that table that end with `tag`, and
	 * with `tag` alone the tags that end with it in every table.
	 *
	 * Every combination:
	 * - `getTags({})`: every tag in the project, ordered by record count,
	 *   descending.
	 * - `getTags({ table: 't' })`: every tag in table 't'.
	 * - `getTags({ table: 't', tag: 'g' })`: exact match on the tag 'g' in
	 *   table 't'.
	 * - `getTags({ tag: 'g' })` with no `table`: the tag 'g' across all
	 *   tables.
	 * - `getTags({ table: 't', tag: 'g', condition: 'gte' })`: prefix, every
	 *   tag in 't' starting with 'g'.
	 * - `getTags({ table: 't', tag: 'g', condition: 'lte' })`: suffix, every
	 *   tag in 't' ending with 'g'.
	 * - `getTags({ tag: 'g', condition: 'lte' })` with no `table`: every tag
	 *   ending with 'g', in every table.
	 * - `getTags({ condition: 'gte' })` with neither `table` nor `tag`: errors
	 *   with '"table" or "tag" is required for condition.'
	 *
	 * When you are looking for a tag rather than listing them all, pass
	 * `gte`: a name recorded two ways, such as "Asian Spice House" and "Asian
	 * Spice House (alias)", is only found by the prefix. Real tags very often
	 * carry a leading name shared with the rest of their data set, a series
	 * name or an artist recorded once plainly and once with a parenthesised
	 * alias, so an exact match finds one spelling and silently misses its
	 * siblings while the prefix finds the family. When you already know the
	 * exact name, omitting `condition` is the exact match you want. Omit the
	 * key to get the default: an explicit undefined or null `condition` is
	 * rejected with INVALID_PARAMETER.
	 *
	 * Refused to a signed out caller when the project's `require_login`
	 * setting is on: REQUIRE_LOGIN from the SDK, INVALID_REQUEST from the
	 * backend for a caller that skips it. A project that has never set the
	 * flag counts as ON. Tag metadata used to be served to anyone who knew
	 * the project id.
	 *
	 * The listing is NOT filtered by access group, for any caller. A stored
	 * tag row is keyed by tag and table name with the access group left out
	 * of the key, so `number_of_records` counts the records in every group
	 * together. Anyone who gets past the sign in check reads the project's
	 * entire tag vocabulary, tags carried only by private or admin records
	 * included. The records behind it stay gated, but a tag name is visible
	 * to every signed in user, so do not put anything secret in one.
	 * @param query Query object used to filter results.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<Tag>>.
	 */
	@formHandler()
	getTags(
		query?: {
			/** Table name */
			table?: string;
			/** Tag name */
			tag?: string;
			/** String query condition for tag name. Omitted: exact match when `table` and `tag` are both given, prefix when only `table` is given. `gte` / `>=`: starts with. `lte` / `<=`: ends with. */
			condition?: Condition;
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<Tag>> {
		return getTags.bind(this)(query, fetchOptions);
	}
	/**
	 * Deletes records by query or record_id and returns deletion results.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<string | DatabaseResponse<RecordData>>.
	 */
	@formHandler()
	deleteRecords(
		params: DelRecordQuery & { private_key?: string },
		fetchOptions?: FetchOptions,
	): Promise<string | DatabaseResponse<RecordData>> {
		return deleteRecords.bind(this)(params, fetchOptions);
	}
	/**
	 * Resends the signup confirmation email for the current pending account.
	 * @returns A promise that resolves to Promise<'SUCCESS: Signup confirmation e-mail has been sent.'>.
	 */
	@formHandler()
	resendSignupConfirmation(): Promise<'SUCCESS: Signup confirmation e-mail has been sent.'> {
		return resendSignupConfirmation.bind(this)();
	}
	/**
	 * Sends an account recovery email and optionally sets a recovery redirect URL.
	 * @param redirect Redirect URL or false to disable redirect.
	 * @returns A promise that resolves to Promise<"SUCCESS: Recovery e-mail has been sent.">.
	 */
	@formHandler()
	recoverAccount(
		/** Redirect url on confirmation success. */
		redirect: boolean | string = false,
	): Promise<'SUCCESS: Recovery e-mail has been sent.'> {
		return recoverAccount.bind(this)(redirect);
	}
	/**
	 * Queries users by supported profile/search fields with pagination.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<UserPublic>>.
	 */
	@formHandler()
	getUsers(
		params?: {
			/** Index name to search. */
			searchFor:
				| 'user_id'
				| 'email'
				| 'phone_number'
				| 'locale'
				| 'name'
				| 'address'
				| 'gender'
				| 'birthdate'
				| 'subscribers'
				| 'timestamp'
				| 'access_group'
				| 'approved';
			/** Index value to search. */
			value: string | number | string[];
			/** Search condition. */
			condition?:
				| '>'
				| '>='
				| '='
				| '<'
				| '<='
				| 'gt'
				| 'gte'
				| 'eq'
				| 'lt'
				| 'lte';
			/** Range of search. */
			range?: string | number;
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<UserPublic>> {
		return getUsers.bind(this)(params, fetchOptions);
	}
	/**
	 * Disables the currently logged-in user account.
	 * @returns A promise that resolves to Promise<'SUCCESS: account has been disabled.'>.
	 */
	@formHandler()
	disableAccount(): Promise<'SUCCESS: account has been disabled.'> {
		return disableAccount.bind(this)();
	}
	/**
	 * Returns the last verified email or reverts to it when revert is requested.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string | UserProfile>.
	 */
	@formHandler()
	lastVerifiedEmail(params?: {
		revert: boolean; // Reverts to last verified e-mail when true.
	}): Promise<string | UserProfile> {
		return lastVerifiedEmail.bind(this)(params);
	}
	/**
	 * Unsubscribes the user from a newsletter group.
	 * Takes a numeric group, "public", "authorized" or the name of a named newsletter group. null unsubscribes from every group.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler()
	unsubscribeNewsletter(params: {
		/** Numeric group, "public", "authorized" or a named newsletter group. null unsubscribes from every group. */
		group: number | 'public' | 'authorized' | (string & {}) | null;
	}): Promise<string> {
		return unsubscribeNewsletter.bind(this)(params);
	}
	// /**
	//  * Sends an administrative newsletter request payload.
	//  * @param params Request parameters.
	//  */
	// @formHandler()
	// adminNewsletterRequest(params) {
	//     return adminNewsletterRequest.bind(this)(params);
	// }
	/**
	 * Registers a web push subscription endpoint for notifications.
	 * @param endpoint Push subscription endpoint URL.
	 * @param keys Web Push key pair values.
	 * @returns A promise that resolves to Promise<'SUCCESS: Subscribed to receive notifications.'>.
	 */
	@formHandler()
	subscribeNotification(
		endpoint: string,
		keys: {
			p256dh: string;
			auth: string;
		},
	): Promise<'SUCCESS: Subscribed to receive notifications.'> {
		return subscribeNotification.bind(this)({ endpoint, keys });
	}
	/**
	 * Removes a registered web push subscription endpoint.
	 * @param endpoint Push subscription endpoint URL.
	 * @param keys Web Push key pair values.
	 * @returns A promise that resolves to Promise<'SUCCESS: Unsubscribed from notifications.'>.
	 */
	@formHandler()
	unsubscribeNotification(
		endpoint: string,
		keys: {
			p256dh: string;
			auth: string;
		},
	): Promise<'SUCCESS: Unsubscribed from notifications.'> {
		return unsubscribeNotification.bind(this)({ endpoint, keys });
	}
	/**
	 * Returns the VAPID public key required for browser push subscription setup.
	 * @returns A promise that resolves to Promise<{ VAPIDPublicKey: string }>.
	 */
	@formHandler()
	vapidPublicKey(): Promise<{ VAPIDPublicKey: string }> {
		return vapidPublicKey.bind(this)();
	}
	/**
	 * Sends push notifications to one or more users. Admins only.
	 * @param params Title and body of the push. Together at most 3072 bytes.
	 * @param user_ids Users to push to, up to 1000. Every device each of them registered with subscribeNotification() gets it. Without it, every registered device of the project gets it.
	 * @returns A promise that resolves to Promise<"SUCCESS: Notification sent.">. The pushes are sent right after it resolves.
	 */
	@formHandler()
	pushNotification(
		params: {
			title: string;
			body: string;
		},
		user_ids?: string | string[],
	): Promise<'SUCCESS: Notification sent.'> {
		return pushNotification.bind(this)(params, user_ids);
	}

	/**
	 * Fetches newsletter delivery records with filters and pagination.
	 * Reads a numeric group, "public", "authorized" or a named newsletter group. A named group is readable by anyone its restriction allows, signed in or not.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<Newsletter>>.
	 */
	@formHandler()
	getNewsletters(
		params?: {
			/**
			 * Search points.<br>
			 * 'message_id' and 'subject' value should be string.<br>
			 * Others numbers.
			 */
			searchFor:
				| 'message_id'
				| 'timestamp'
				| 'read'
				| 'complaint'
				| 'bounced'
				| 'subject';
			value: string | number;
			range?: string | number;
			/**
			 * Defaults to '=',
			 * Condition does not work with range.
			 */
			condition?:
				| '>'
				| '>='
				| '='
				| '<'
				| '<='
				| 'gt'
				| 'gte'
				| 'eq'
				| 'lt'
				| 'lte';
			/** Numeric group, "public", "authorized" or a named newsletter group. */
			group: 'public' | 'authorized' | number | (string & {});
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<Newsletter>> {
		return getNewsletters.bind(this)(params, fetchOptions);
	}
	/**
	 * Gets newsletter subscription status for the requested groups.
	 * Takes a numeric group, "public", "authorized" or a named newsletter group. Omit the group for every group the user is subscribed to.
	 * The project owner, Skapi staff and access group 99 admins get every subscriber of the group instead, and can pass "email" to get only the subscribers whose e-mail address starts with it.
	 * An admin in access groups 90 ~ 98 gets the subscriber list too, but reads it through a privacy layer:
	 * "subscribed_email" is masked ("j**@**.com") and the mask is lossy, so two different subscribers can read the same;
	 * "subscriber_token" comes with each masked row as an opaque, stable, per address key, and it is the only value that tells such rows apart, so key lists and selections on it, never on the masked address;
	 * the token is not a readable address and is scoped to this service, owner and group, so it cannot be matched against a token from another group or project, and it is a key for working with a listing, not an id to store: the platform can reissue tokens;
	 * "startKey" is sealed by the server and has to be handed back verbatim, which fetchMore already does;
	 * and "email" is refused with "No access.".
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<{ active: boolean; timestamp: number; group: number | string; subscribed_email: string; subscriber_token?: string; }[]>.
	 */
	@formHandler()
	getNewsletterSubscription(
		params?: {
			/** Numeric group, "public", "authorized" or a named newsletter group. Omit or null for every group. */
			group?: number | 'public' | 'authorized' | (string & {}) | null;
			/**
			 * Another user's subscriptions. The project owner's account, Skapi staff and
			 * admins (access groups 90 ~ 99) only. An admin in access groups 90 ~ 98 reads
			 * that user's address masked.
			 */
			user_id?: string;
			/** Owner, Skapi staff and access group 99 only. Returns the subscribers of "group" whose e-mail address starts with this text. Requires "group", cannot be used with "user_id". Admins 90 ~ 98 are refused with "No access.". */
			email?: string;
		},
		fetchOptions?: FetchOptions,
	): Promise<
		| {
			active: boolean;
			/** Undefined on a group's whole subscriber list, which the index does not carry it on. */
			timestamp: number;
			group: number | string;
			subscribed_email: string;
			/** Only when the address above is masked. Opaque, stable per address key. */
			subscriber_token?: string;
		}[]
		| DatabaseResponse<{
			active: boolean;
			/** Undefined on a group's whole subscriber list, which the index does not carry it on. */
			timestamp: number;
			group: number | string;
			subscribed_email: string;
			/** Only when the address above is masked. Opaque, stable per address key. */
			subscriber_token?: string;
		}>
	> {
		return getNewsletterSubscription.bind(this)(params, fetchOptions);
	}
	/**
	 * Reports whether client-side record encryption is on, and whether it is
	 * currently unlocked.
	 * @returns { status: 'disabled' | 'locked' | 'unlocked', reason?, user_id?, fingerprint? }
	 */
	getEncryptionStatus(): {
		status: string;
		reason?: string;
		user_id?: string;
		fingerprint?: string;
	} {
		return getEncryptionStatus.bind(this)();
	}
	/**
	 * True when a record's `data` is the placeholder returned in place of
	 * content this session cannot decrypt.
	 *
	 * Only relevant with `encryption: { withheld: 'sentinel' }`. Under the
	 * default, withheld data is `null` and an ordinary falsy check is enough.
	 * @param data The value of `record.data`.
	 * @returns boolean
	 */
	isWithheld(data: any): boolean {
		return isWithheld.call(this, data);
	}
	/**
	 * Unlocks record encryption with the user's password.
	 *
	 * Normally unnecessary: logging in unlocks automatically, and a page reload
	 * unlocks from the device store. This is for a session restored from a token
	 * on a device that has never been unlocked, and for Node, which has no
	 * IndexedDB and is therefore always locked after a token restore.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<{ status: string }>.
	 */
	@formHandler()
	unlockEncryption(params: { password: string }): Promise<{ status: string }> {
		return unlockEncryption.bind(this)(params);
	}
	/**
	 * Drops encryption keys from memory without logging out. Pass
	 * { forgetDevice: true } to also clear the device store, which means the
	 * next reload will require the password again.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<{ status: string }>.
	 */
	lockEncryption(params?: { forgetDevice?: boolean }): Promise<{ status: string }> {
		return lockEncryption.bind(this)(params);
	}
	/**
	 * Collects a freshly minted recovery code, ONCE.
	 *
	 * Call it right after a login or signup that may have enrolled the user; it
	 * returns the code and forgets it. There is no way to fetch it again later,
	 * and that is the point: if the SDK could hand it back on demand it would be
	 * holding the key, and so could the service provider. Show it, make the user
	 * confirm they saved it, and never send it anywhere.
	 * @returns The code, or null if nothing was enrolled.
	 */
	takeRecoveryCode(): string | null {
		return takeRecoveryCode.bind(this)();
	}
	/**
	 * Unlocks with a recovery code after a forgotten-password reset, and repairs
	 * the keyring for the new password.
	 *
	 * The order is: reset the password, log in with the new one (encryption will
	 * report 'locked'), then call this with the code and that new password. A
	 * used code is retired and a replacement is returned.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<{ status, repaired, recoveryCode }>.
	 */
	@formHandler()
	unlockWithRecoveryCode(params: {
		code: string;
		password?: string;
	}): Promise<{ status: string; repaired: boolean; recoveryCode: string | null }> {
		return unlockWithRecoveryCode.bind(this)(params);
	}
	/**
	 * Retires the current recovery code and issues a new one. Requires an
	 * unlocked session: only someone who can already decrypt can mint a code.
	 * @returns A promise that resolves to Promise<{ recoveryCode: string }>.
	 */
	regenerateRecoveryCode(): Promise<{ recoveryCode: string }> {
		return regenerateRecoveryCode.bind(this)();
	}
	/**
	 * Pins another user's public key fingerprint after verifying it out of band.
	 *
	 * Only needed when a peer's key has changed, or when trustPolicy is
	 * 'strict'. The provider serves the key directory, so a changed key is both
	 * what a legitimate account reset looks like and what a key substitution
	 * attack looks like: the SDK refuses to guess.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<void>.
	 */
	pinPeerKey(params: { user_id: string; fingerprint: string }): Promise<void> {
		return pinPeerKey.bind(this)(params);
	}
	/**
	 * Grants users access to a private record.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler()
	grantPrivateRecordAccess(params: {
		record_id: string;
		user_id: string | string[];
	}): Promise<string> {
		return grantPrivateRecordAccess.bind(this)(params);
	}
	/**
	 * Removes previously granted private record access.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler()
	removePrivateRecordAccess(params: {
		record_id: string;
		user_id: string | string[];
	}): Promise<string> {
		return removePrivateRecordAccess.bind(this)(params);
	}
	/**
	 * Lists current private record access grants.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<DatabaseResponse<{ record_id: string; user_id: string; }>>.
	 */
	@formHandler()
	listPrivateRecordAccess(
		params: {
			record_id?: string;
			user_id?: string | string[];
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<{ record_id: string; user_id: string }>> {
		return listPrivateRecordAccess.bind(this)(params, fetchOptions);
	}
	/**
	 * Requests a temporary access key for reading a private record.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler()
	requestPrivateRecordAccessKey(params: {
		record_id: string;
		reference_id?: string;
	}): Promise<string> {
		return requestPrivateRecordAccessKey.bind(this)(params);
	}
	/**
	 * Deletes uploaded files by endpoint list.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<RecordData[]>.
	 */
	@formHandler()
	deleteFiles(params: {
		endpoints: string | string[]; // bin file endpoints
	}): Promise<RecordData[]> {
		return deleteFiles.bind(this)(params);
	}
	/**
	 * Uploads files to an existing record. Resolves with the files stored (`completed`), the files storage did not accept (`failed`) and why (`errors`, one entry per failed file with a SkapiError that carries the storage code).
	 * `timeout` (milliseconds) aborts a file that takes longer and counts it as failed with the code 'TIMEOUT'; by default a file has no time limit.
	 * `throwOnFileError` rejects at the first file that fails, with that file's error, instead of resolving; the files stored before it stay stored.
	 * @param fileList Files or form event containing files.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<UploadFilesResult>.
	 */
	@formHandler()
	uploadFiles(
		fileList: FormData | HTMLFormElement | SubmitEvent,
		params: UploadFilesParams,
	): Promise<UploadFilesResult> {
		return uploadFiles.bind(this)(fileList, params);
	}
	/**
	 * Calls the mock endpoint for testing request and error handling flows.
	 * @param data Data payload for the request.
	 * @param options Optional behavior configuration.
	 * @returns A promise that resolves to Promise<{ [key: string]: any }>.
	 */
	@formHandler()
	mock(
		data: Form<
			| any
			| {
					raise:
						| 'ERR_INVALID_REQUEST'
						| 'ERR_INVALID_PARAMETER'
						| 'SOMETHING_WENT_WRONG'
						| 'ERR_EXISTS'
						| 'ERR_NOT_EXISTS';
			  }
		>,
		options?: {
			auth?: boolean;
			method?: string;
			responseType?:
				| 'blob'
				| 'json'
				| 'text'
				| 'arrayBuffer'
				| 'formData'
				| 'document';
			contentType?: string;
			tokenHeaders?: {
				accessToken?: boolean | string;
				idToken?: boolean | string;
			};
			progress?: ProgressCallback;
		},
	): Promise<{ [key: string]: any }> {
		return mock.bind(this)(data, options);
	}
	/**
	 * Authenticates a user with email/username and password.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<UserProfile>.
	 */
	@formHandler({ preventMultipleCalls: true })
	login(
		params: Form<{
			/** if given, username will be used instead of email. A username always logs its account in. */
			username?: string;
			/**
			 * E-Mail for signin. 64 character max. The e-mail an account without a username
			 * was created with always logs it in. Any other e-mail (that of an account created
			 * with a username, or a changed e-mail) logs in only once it is verified (verifyEmail()).
			 */
			email: string;
			/** Password for signin. Should be at least 6 characters. */
			password: string;
		}>,
	): Promise<UserProfile> {
		return login.bind(this)(params);
	}
	/**
	 * Logs out the current user session (optionally globally).
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<'SUCCESS: The user has been logged out.'>.
	 */
	@formHandler()
	logout(
		params?: Form<{ global: boolean }>,
	): Promise<'SUCCESS: The user has been logged out.'> {
		return logout.bind(this)(params);
	}

	/**
	 * Creates a new user account with optional signup and login behavior settings.
	 * @param params Payload for the request.
	 * @param option Optional behavior configuration.
	 * @returns A promise that resolves to Promise<UserProfile | "SUCCESS: The account has been created. User's signup confirmation is required." | 'SUCCESS: The account has been created.'>.
	 */
	@formHandler({ preventMultipleCalls: true })
	signup(
		params: Form<UserAttributes & {
			/** Required. Always. */
			email: string
			password: String
			/**
			 * Optional. When given it becomes the account's PERMANENT login
			 * username, which always logs the account in and can never be changed.
			 * The e-mail also logs the account in, but only once it is verified:
			 * opening the signup confirmation link verifies it, and without signup
			 * confirmation the user verifies it with verifyEmail() after logging in
			 * with the username. After an e-mail change the new e-mail logs in once
			 * it is verified the same way. E-mail login is not enabled while that
			 * e-mail is already another account's login ID.
			 */
			username?: string
		}>,
		option?: {
			/**
			 * When true, the service will send out confirmation E-Mail.
			 * User will not be able to signin to their account unless they have confirm their email.
			 * Parameter also accepts URL string for user to be taken to when clicked on confirmation link.
			 * Default is false.
			 */
			signup_confirmation?: boolean | string;
			/**
			 * The newsletter the user is subscribed to once they confirm their e-mail, with no
			 * newsletter confirmation e-mail of its own.
			 * `0` or `'public'`: the public newsletter. `1` or `'authorized'`: Service Email.
			 * The name of a named newsletter group: that group, which has to exist and whose
			 * restriction the account has to meet. `true`: Service Email, as before.
			 * User's signup confirmation is required for this parameter: a user whose e-mail is
			 * never confirmed is never subscribed.
			 * Default is false.
			 */
			email_subscription?: boolean | number | string;
			/**
			 * Automatically login to account after signup. Will not work if signup confirmation is required.
			 */
			login?: boolean;
			template?: {
				/** message_id of the template to use for the signup confirmation e-mail. */
				signup_confirmation?: string;
				/** message_id of the template to use for the welcome e-mail (sent on the user's first confirmed login). */
				welcome?: string;
			};
		},
	): Promise<
		| UserProfile
		| "SUCCESS: The account has been created. User's signup confirmation is required."
		| 'SUCCESS: The account has been created.'
	> {
		return signup.bind(this)(params, option);
	}

	/**
	 * Resets a password using email, verification code, and new password.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<"SUCCESS: New password has been set.">.
	 */
	@formHandler({ preventMultipleCalls: true })
	resetPassword(
		params: Form<{
			/** Signin E-Mail */
			email: string;
			/** The verification code user has received. */
			code: string | number;
			/** New password to set. Verification code is required. */
			new_password: string;
		}>,
	): Promise<'SUCCESS: New password has been set.'> {
		return resetPassword.bind(this)(params);
	}
	/**
	 * Verifies the user email address with a confirmation code.
	 * Call it without `code` to send the code, then with the code the user received.
	 * Once the e-mail is verified it also logs the account in (a few seconds after
	 * the verification succeeds) when the account was created with a username or its
	 * e-mail was changed, unless that e-mail is a login ID another account of the
	 * project was granted: the address that account was created or invited with, or
	 * one it has verified. An e-mail login another account holds without having
	 * verified the address is removed, and this account gets the login.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler({ preventMultipleCalls: true })
	verifyEmail(params?: Form<{ code: string }>, options?: { template?: { verification?: string } }): Promise<string> {
		// 'SUCCESS: Verification code has been sent.' | 'SUCCESS: "email" is verified.'
		return verifyEmail.bind(this)(params, options);
	}
	/**
	 * Verifies the user phone number with a confirmation code.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler({ preventMultipleCalls: true })
	verifyPhoneNumber(params?: Form<{ code: string }>, options?: { template?: { verification?: string } }): Promise<string> {
		// 'SUCCESS: Verification code has been sent.' | 'SUCCESS: "phone_number" is verified.'
		return verifyPhoneNumber.bind(this)(params, options);
	}
	/**
	 * Sends a password reset code to the user's e-mail. The e-mail comes from the service's
	 * "forgot_password" template (the Password Reset Email on the Automated Emails page), or
	 * from `options.template.forgot_password` for this call alone; a message_id that does not
	 * exist falls back to the template in use, or the built-in default.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<"SUCCESS: Verification code has been sent.">.
	 */
	@formHandler({ preventMultipleCalls: true })
	forgotPassword(
		params: Form<{
			/** Signin E-Mail. */
			email: string;
		}>,
		options?: {
			template?: {
				/** message_id of the password reset template to use for this call. */
				forgot_password?: string;
			};
		},
	): Promise<'SUCCESS: Verification code has been sent.'> {
		return forgotPassword.bind(this)(params, options);
	}
	/**
	 * Changes password for the authenticated user.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<'SUCCESS: Password has been changed.'>.
	 */
	@formHandler({ preventMultipleCalls: true })
	changePassword(params: {
		new_password: string;
		current_password: string;
	}): Promise<'SUCCESS: Password has been changed.'> {
		return changePassword.bind(this)(params);
	}
	/**
	 * Updates profile attributes for the authenticated user.
	 *
	 * A changed e-mail is written unverified, and it logs the account in only once
	 * it is verified with verifyEmail(): the login follows a few seconds after the
	 * verification succeeds. The previous e-mail stops logging in right after the
	 * change, unless it is the e-mail an account without a username was created with,
	 * which stays its login ID. A username always logs in. E-mail login is not added
	 * while the new e-mail is a login ID another account of the project was granted:
	 * the address that account was created or invited with, or one it has verified.
	 * An e-mail login another account holds without having verified the address is
	 * removed instead, and this account gets the login.
	 *
	 * With another user's `user_id` it updates that user from admin context and
	 * follows the rules of updateUserAttributes(). Your own `user_id` is ignored.
	 * @param params Payload for the request.
	 * @returns A promise that resolves to Promise<UserProfile>.
	 */
	@formHandler({ preventMultipleCalls: true })
	updateProfile(params: Form<UserAttributes>): Promise<UserProfile> {
		return updateProfile.bind(this)(params);
	}
	/**
	 * Creates or updates a database record with optional file uploads.
	 * @param params Payload for the request.
	 * @param config Required record configuration (table, record_id, index, tags, source, reference, etc.).
	 * @param files File list to process.
	 * @returns A promise that resolves to Promise<RecordData>.
	 */
	@formHandler()
	postRecord(
		params: Form<Record<string, any>> | null | undefined,
		config: PostRecordConfig,
		files?: { name: string; file: File }[],
	): Promise<RecordData> {
		return postRecord.bind(this)(params, config, files);
	}
	/**
	 * Creates or updates several records in one call, 100 records to a request. Each item is the two arguments of postRecord() as one object: `{ data, config }`.
	 * Resolves with `list`, the records that were saved in the order their items were given, and `failed`, the items that were not saved, each with its `index` in the input, a `code` and a `message`.
	 * A refused item never rejects the call, and the items after it are still posted; only a failure of a request itself (connection, sign-in, a server error) rejects, and the records saved before it stay saved.
	 * Files are not attached here: use uploadFiles() on a saved record.
	 * @param items Non-empty array of `{ data, config }` items.
	 * @returns A promise that resolves to Promise<PostRecordsResult>.
	 */
	postRecords(items: PostRecordsItem[]): Promise<PostRecordsResult> {
		return postRecords.bind(this)(items);
	}
	/**
	 * Fetches subscriber/subscription relationships with filters and pagination. At least one of `subscriber` or `subscription` must be provided.
	 * @param params Request parameters.
	 * @param fetchOptions Pagination and fetch behavior options.
	 * @returns A promise that resolves to Promise<DatabaseResponse<Subscription>>.
	 */
	@formHandler()
	getSubscriptions(
		params: {
			/** Subscribers user id. */
			subscriber?: string;
			/** User ID of the subscription. User id that subscriber has subscribed to. */
			subscription?: string;
			/** Fetch blocked subscription when True */
			blocked?: boolean;
		},
		fetchOptions?: FetchOptions,
	): Promise<DatabaseResponse<Subscription>> {
		return getSubscriptions.bind(this)(params, fetchOptions);
	}
	/**
	 * Subscribes to another user with optional feed/notification/email preferences.
	 * Calling it again on the same user changes only the options given and keeps the rest,
	 * so `get_notified` can be turned on or off without touching `get_feed`.
	 * @param params Request parameters. `get_feed`: the user's records posted with `upload_to_feed` appear in getFeed(). `get_notified`: push notifications for the user's records posted with `notify_subscribers`, and for new references to their records with `notify_referencing_records` (the device also needs subscribeNotification()). A new subscription starts with every option off.
	 * @returns A promise that resolves to Promise<Subscription>.
	 */
	@formHandler()
	subscribe(params: {
		user_id: string;
		get_feed?: boolean;
		get_notified?: boolean;
		get_email?: boolean;
	}): Promise<Subscription> {
		return subscribe.bind(this)(params);
	}
	/**
	 * Unsubscribes from another user.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<'SUCCESS: The user has unsubscribed.'>.
	 */
	@formHandler()
	unsubscribe(params: {
		user_id: string;
	}): Promise<'SUCCESS: The user has unsubscribed.'> {
		return unsubscribe.bind(this)(params);
	}
	/**
	 * Blocks a subscriber user_id.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<'SUCCESS: Blocked user ID "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx".'>.
	 */
	@formHandler()
	blockSubscriber(params: {
		user_id: string;
	}): Promise<'SUCCESS: Blocked user ID "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx".'> {
		return blockSubscriber.bind(this)(params);
	}
	/**
	 * Unblocks a previously blocked subscriber user_id.
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<'SUCCESS: Unblocked user ID "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx".'>.
	 */
	@formHandler()
	unblockSubscriber(params: {
		user_id: string;
	}): Promise<'SUCCESS: Unblocked user ID "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx".'> {
		return unblockSubscriber.bind(this)(params);
	}
	/**
	 * Subscribes an email/user to a newsletter group with optional redirect flow.
	 * Takes a numeric group, "public", "authorized" or the name of a named newsletter group registered with registerNewsletterGroup().
	 * @param params Request parameters.
	 * @returns A promise that resolves to Promise<string>.
	 */
	@formHandler()
	subscribeNewsletter(
		params: Form<{
			email?: string | string[];
			/** Numeric group, "public", "authorized" or a named newsletter group. */
			group: number | 'public' | 'authorized' | (string & {});
			redirect?: string;
		}>,
	): Promise<string> {
		return subscribeNewsletter.bind(this)(params);
	}

	/**
	 * Creates or updates multiple database records in a single bulk request. Requires a non-empty array, and all entries must share the same table/service.
	 * @param config Non-empty array of record configurations to post.
	 * @returns A promise that resolves to Promise<RecordData[]>.
	 */
	bulkPostRecords(config: PostRecordConfig[]): Promise<RecordData[]> {
		return bulkPostRecords.bind(this)(config);
	}
}
